/**
 * AccessibleFolderCacheStore — Cached folder list for cloud workspaces.
 *
 * Server-authoritative, bounded folder metadata cache. Folder list metadata is
 * persisted by WorkspaceMetadataStore; signed grants remain owned by
 * PermissionSnapshotStore.
 *
 * Used by FolderStore as the non-owner folder metadata source and by the
 * Owner/Admin path to filter private system rows. Non-owner clients do not sync
 * Y.js `db$folders`; this server-filtered cache is their local-first route.
 *
 * Simpler than the doc cache because:
 * - Local folder creation is optimistic and pinned until server acknowledgement
 * - Trash filtering is server-side (myAccessibleFolders excludes trashed folders)
 * - No revocation notifications (UI just hides inaccessible folders)
 *
 * Local-First guarantees:
 * - Cache persists across reload via IDB (sidebar populated instantly)
 * - GraphQL sync merges server state when back online
 * - Reconnect triggers fresh fetch
 * - Visibility change (tab focus) triggers refresh for cross-client sync
 * - Periodic background sync every 30s as safety net
 */

// Direct import to avoid circular dependency
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import { LiveData, Store } from '@toeverything/infra';
import {
  combineLatest,
  map,
  type Observable,
  Subject,
  type Subscription,
} from 'rxjs';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import {
  isBackendAuthOrPermissionError,
  isBackendKnownUnavailable,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import { readWorkspaceCached } from '../../cloud/services/workspace-read-cache';
import type { FolderPermissions } from '../../folder-api/services/folder-api';
import type {
  PermissionSnapshotResponse,
  PermissionSnapshotStore,
} from '../../permissions/stores/permission-snapshot';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import {
  resolveWorkspaceLocalAuthorityAccountId,
  workspacePermissionCacheOwnerKey,
} from '../../workspace/utils/permission-cache-identity';
import { readPermissionGraphSnapshotWithCatchUp } from '../../workspace/utils/permission-graph-catch-up';
import {
  getPermissionGraphSnapshotVersion,
  mergePermissionGraphVersion,
  shouldIgnorePermissionGraphSnapshot,
} from '../../workspace/utils/permission-graph-version';
import { filterUserVisibleDocIds } from '../../workspace/utils/user-visible-docs';
import {
  fetchWorkspaceMetadataDeltaHeadToken,
  fetchWorkspaceMetadataDeltaPage,
  type WorkspaceFolderChildrenPage,
  type WorkspaceListMetadata,
  type WorkspaceMetadataDeltaPage,
  type WorkspaceMetadataItem,
  type WorkspaceMetadataPage,
  type WorkspaceMetadataPendingMutation,
  WorkspaceMetadataStore,
} from '../../workspace-metadata';
import { recordWorkspaceProjectionDiagnostic } from '../../workspace-projection-reconciliation/diagnostics';
import type {
  WorkspaceMetadataChange,
  WorkspacePermissionChange,
} from '../../workspace-projection-reconciliation/types';

const PRIVATE_AREA_ROOTS_SYSTEM_KIND = 'private_area_roots';
const UNKNOWN_SERVER_REVISION = '1970-01-01T00:00:00.000Z';
const FOLDER_CHILDREN_COMPLETED_REUSE_MS = 500;
const FOLDER_SHAPE_METADATA_CHANGES = new Set([
  'doc_created',
  'doc_moved',
  'doc_trashed',
  'doc_restored',
  'doc_visibility_changed',
  'folder_created',
  'folder_moved',
  'folder_trashed',
  'folder_restored',
  'folder_deleted',
]);
let nextFolderChildrenReadScope = 0;

export interface AccessibleFolderOptimisticSnapshot {
  folders: AccessibleFolderMeta[];
  rootFolderDocIds: string[];
  optimisticCreatedFolders: AccessibleFolderMeta[];
  pendingDocFolderAssignments: Array<{
    docId: string;
    folderId: string;
    createdAt: number;
  }>;
}

export interface AccessibleFolderOptimisticTrashRecord {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  docIds: string[];
  folders: AccessibleFolderMeta[];
  trashedAt: string;
}

interface PendingDocFolderAssignment {
  docId: string;
  folderId: string;
  createdAt: number;
}

interface AccessibleFolderOrderState {
  folderIds: string[];
  folderDocIds: Record<string, string[]>;
}

interface OptimisticFolderCreateIfMissing {
  inheritPermissions?: boolean;
  name: string;
  ownerUserId?: string | null;
  systemKind?: string | null;
  visibility?: string;
}

interface OptimisticFolderUpsertOptions {
  preserveUntilServerAck?: boolean;
}

function insertDocAtPosition(
  docIds: string[],
  docId: string,
  position?: { at: 'before' | 'after'; docId: string }
): string[] {
  if (!position) {
    return [docId, ...docIds];
  }
  const referenceIndex = docIds.indexOf(position.docId);
  if (referenceIndex === -1) {
    return [docId, ...docIds];
  }
  const next = [...docIds];
  next.splice(
    position.at === 'after' ? referenceIndex + 1 : referenceIndex,
    0,
    docId
  );
  return next;
}

export interface AccessibleFolderMeta {
  id: string;
  name: string;
  parentId: string | null;
  ownerUserId: string | null;
  systemKind: string | null;
  visibility: string;
  inheritPermissions: boolean;
  isWorkspaceRoot: boolean;
  immutable: boolean;
  storageLimitBytes: number | null;
  storageUsedBytes: number | null;
  metadataOnly: boolean;
  hasChildren?: boolean;
  /** sl-oiya.5: IDs of docs linked to this folder (from GraphQL). */
  docIds: string[];
  permissionGraphVersion?: number;
  revision?: string | null;
}

export interface AccessibleTrashedFolderMeta {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  systemKind: string | null;
  trashedAt: string | null;
  trashedBy: string | null;
  createdAt: string;
  updatedAt: string;
  docIds: string[];
  hasChildren: boolean;
  childCount: number | null;
  subtreeRevision: string | null;
  permissionGraphVersion: number;
  permissions: FolderPermissions | null;
  folderMetadata?: WorkspaceMetadataItem['folder'] | null;
}

export interface FolderPermissionReconciliationResult {
  grantedDocIds: string[];
  revokedDocIds: string[];
}

function isProjectionSystemContainer(folder: {
  systemKind?: string | null;
}): boolean {
  return folder.systemKind === PRIVATE_AREA_ROOTS_SYSTEM_KIND;
}

function permissionEventMayChangeFolderDetails(changeType: string): boolean {
  return (
    changeType === 'folder_role_granted' ||
    changeType === 'folder_role_revoked' ||
    changeType === 'area_role_granted' ||
    changeType === 'area_role_revoked' ||
    changeType === 'folder_inheritance_changed' ||
    changeType === 'folder_visibility_changed' ||
    changeType === 'folder_metadata_updated'
  );
}

export class AccessibleFolderCacheStore extends Store {
  private readonly folderDetailsInvalidated$ =
    new Subject<ReadonlySet<string> | null>();
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly globalState?: GlobalState,
    private readonly permissionSnapshotStore?: PermissionSnapshotStore
  ) {
    super();
  }

  private readonly cache$ = new LiveData<AccessibleFolderMeta[]>([]);
  private readonly trashedFolders$ = new LiveData<
    AccessibleTrashedFolderMeta[]
  >([]);
  private readonly rootFolderDocIds$ = new LiveData<string[]>([]);
  private readonly optimisticTrashedFolders$ = new LiveData<
    AccessibleFolderOptimisticTrashRecord[]
  >([]);
  private readonly pendingDocFolderAssignments$ = new LiveData<
    PendingDocFolderAssignment[]
  >([]);
  private readonly optimisticCreatedFolders$ = new LiveData<
    AccessibleFolderMeta[]
  >([]);
  private fetchTriggered = false;
  private fetchAccessibleFoldersPromise: Promise<void> | null = null;
  private fetchAccessibleFoldersAgain = false;
  private readonly lifecycleAbortController = new AbortController();
  private disposed = false;
  private idbLoaded = false;
  private serverFetchCompleted = false;
  private authoritativeAccessDenied = false;
  private permissionGraphVersion = 0;
  private readonly folderChildrenNextCursor = new Map<string, string | null>();
  private readonly loadedFolderChildren = new Set<string>();
  private readonly folderChildrenReadScope = nextFolderChildrenReadScope++;
  private folderChildrenReadGeneration = 0;
  private readonly loadedTrashedFolderChildren = new Set<string>();
  private readonly trashedFolderChildrenNextCursor = new Map<
    string,
    string | null
  >();
  private trashedFoldersNextCursor: string | null = null;
  private trashHydratedStore: WorkspaceMetadataStore | null = null;
  private readonly trashedFolderRootIds = new Set<string>();
  private fetchTrashedFoldersPromise: Promise<void> | null = null;
  private metadataDeltaToken: string | null = null;
  private metadataReconciliation: Promise<void> = Promise.resolve();
  private readonly metadataStoreRefs = new Map<
    string,
    {
      scopeKey: string;
      store: WorkspaceMetadataStore;
    }
  >();
  private pendingStateWrite: Promise<void> = Promise.resolve();

  private get authorityStore(): PermissionSnapshotStore | null {
    return this.permissionSnapshotStore &&
      typeof this.permissionSnapshotStore.getVerifiedTenantId === 'function'
      ? this.permissionSnapshotStore
      : null;
  }

  getLocalFolderPermissions(folderId: string): FolderPermissions | null {
    return (
      (this.authorityStore?.getFolderPermissions(
        folderId
      ) as FolderPermissions | null) ?? null
    );
  }

  subscribeToFolderDetailsInvalidations(
    folderId: string,
    callback: () => void
  ): Subscription {
    return this.folderDetailsInvalidated$.subscribe(folderIds => {
      if (folderIds === null || folderIds.has(folderId)) callback();
    });
  }

  private reconcileMetadata<T>(apply: () => Promise<T>): Promise<T> {
    const result = this.metadataReconciliation.then(apply, apply);
    this.metadataReconciliation = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  observePermissionGraphVersion(version?: number | null): void {
    this.permissionGraphVersion = mergePermissionGraphVersion(
      this.permissionGraphVersion,
      version ?? 0
    );
  }

  readonly isPopulated$ = new LiveData<boolean>(false);
  /**
   * True after the account-scoped IndexedDB folder cache has been checked.
   * UI can use this to wait for warm local folder data without gating on
   * network when there is no local cache yet.
   */
  readonly isLocalCacheLoaded$ = new LiveData<boolean>(false);

  /**
   * True after the current folder projection has completed an authoritative
   * server read. An IDB-only or exhausted-retry state is intentionally not a
   * confirmation that an empty workspace is truly empty.
   */
  readonly isServerConfirmed$ = new LiveData<boolean>(false);
  readonly hasNextTrashedFolderPage$ = new LiveData<boolean>(false);
  /**
   * Folders whose first child window is being read. A Folder leaves the set
   * once that read settles, so a failed read never looks like loading forever.
   */
  readonly initialChildrenLoadingFolderIds$ = new LiveData<ReadonlySet<string>>(
    new Set()
  );

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineHandler: (() => void) | null = null;
  private backendAvailabilityUnsubscribe: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private permissionRefreshPromise: Promise<void> = Promise.resolve();
  private completedAuthoritativeProjectionVersion = 0;
  private readonly scopedFolderRefreshIds = new Set<string>();
  private scopedFolderRefreshScheduled = false;

  private metadataStoreFor(shapeKey: string): WorkspaceMetadataStore | null {
    const workspace = this.workspaceService.workspace;
    const server = this.workspaceServerService.server;
    if (!workspace || workspace.flavour === 'local' || !server) {
      return null;
    }
    const accountId = resolveWorkspaceLocalAuthorityAccountId({
      liveAccountId: server.confirmedAccountId,
      cachedAccountId: server.cachedAccountId,
      scopeReader: this.globalState,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId: workspace.id,
    });
    if (!accountId) return null;
    const authorityStore = this.authorityStore;
    const verifiedTenantId = authorityStore?.getVerifiedTenantId();
    if (authorityStore && !verifiedTenantId) return null;
    const scope = {
      accountId,
      principalId: accountId,
      serverId: server.id,
      serverBaseUrl: server.baseUrl,
      visibilityMode: 'user_effective' as const,
      permissionGraphVersion: this.permissionGraphVersion,
      workspaceId: workspace.id,
      tenantId: verifiedTenantId ?? `unbound-test:${workspace.id}`,
      shapeKey,
    };
    const scopeKey = JSON.stringify(scope);
    const current = this.metadataStoreRefs.get(shapeKey);
    if (current?.scopeKey === scopeKey) {
      return current.store;
    }
    const store = new WorkspaceMetadataStore(scope);
    this.metadataStoreRefs.set(shapeKey, { scopeKey, store });
    return store;
  }

  private get metadataStore(): WorkspaceMetadataStore | null {
    return this.metadataStoreFor('folder-tree');
  }

  private get trashMetadataStore(): WorkspaceMetadataStore | null {
    return this.metadataStoreFor('trash-folders-window');
  }

  private get workspaceId(): string | null {
    return this.workspaceService.workspace?.id ?? null;
  }

  private get cacheOwnerKey(): string | null {
    const workspace = this.workspaceService.workspace;
    const server = this.workspaceServerService.server;
    if (!workspace || workspace.flavour === 'local' || !server) {
      return null;
    }
    const accountId = resolveWorkspaceLocalAuthorityAccountId({
      liveAccountId: server.confirmedAccountId,
      cachedAccountId: server.cachedAccountId,
      scopeReader: this.globalState,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId: workspace.id,
    });
    if (!accountId) return null;
    return workspacePermissionCacheOwnerKey({
      accountId,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
    });
  }

  private get orderStorageKey(): string | null {
    const wsId = this.workspaceId;
    const cacheOwnerKey = this.cacheOwnerKey;
    if (!wsId || !cacheOwnerKey) {
      return null;
    }
    return `streamlain:accessible-folder-order:${wsId}:${cacheOwnerKey}`;
  }

  private cloneFolderMeta(folder: AccessibleFolderMeta): AccessibleFolderMeta {
    return {
      ...folder,
      docIds: [...folder.docIds],
    };
  }

  private cloneTrashRecord(
    record: AccessibleFolderOptimisticTrashRecord
  ): AccessibleFolderOptimisticTrashRecord {
    return {
      ...record,
      docIds: [...record.docIds],
      folders: record.folders.map(folder => this.cloneFolderMeta(folder)),
    };
  }

  private clonePendingDocAssignment(
    assignment: PendingDocFolderAssignment
  ): PendingDocFolderAssignment {
    return { ...assignment };
  }

  private setOptimisticCreatedFolders(folders: AccessibleFolderMeta[]): void {
    const seen = new Set<string>();
    const next: AccessibleFolderMeta[] = [];
    for (const folder of folders) {
      if (seen.has(folder.id)) {
        continue;
      }
      seen.add(folder.id);
      next.push(this.cloneFolderMeta(folder));
    }
    this.optimisticCreatedFolders$.setValue(next);
  }

  private rememberOptimisticCreatedFolder(folder: AccessibleFolderMeta): void {
    this.setOptimisticCreatedFolders([
      folder,
      ...this.optimisticCreatedFolders$.value.filter(f => f.id !== folder.id),
    ]);
  }

  private mergeOptimisticCreatedFolders(
    serverFolders: AccessibleFolderMeta[]
  ): AccessibleFolderMeta[] {
    const pending = this.optimisticCreatedFolders$.value;
    if (pending.length === 0) {
      return serverFolders;
    }

    const serverFoldersById = new Map(
      serverFolders.map(folder => [folder.id, folder])
    );
    const stillPending = pending.filter(
      folder =>
        !this.isOptimisticCreatedFolderServerAcked(
          serverFoldersById.get(folder.id)
        )
    );
    if (stillPending.length !== pending.length) {
      this.setOptimisticCreatedFolders(stillPending);
    }
    if (stillPending.length === 0) {
      return serverFolders;
    }

    const pendingIds = new Set(stillPending.map(folder => folder.id));
    return this.applyOrderState([
      ...stillPending.map(folder => this.cloneFolderMeta(folder)),
      ...serverFolders.filter(folder => !pendingIds.has(folder.id)),
    ]);
  }

  private isOptimisticCreatedFolderServerAcked(
    serverFolder: AccessibleFolderMeta | undefined
  ): boolean {
    // The server materializing this immutable folder ID is the create ACK.
    // Later local rename/move operations have their own pending mutations in
    // WorkspaceMetadataStore. Keeping this separate create snapshot until all
    // mutable fields also match can resurrect stale name/access/placement data.
    return !!serverFolder && !serverFolder.metadataOnly;
  }

  private updateOptimisticCreatedFolder(
    folderId: string,
    updater: (folder: AccessibleFolderMeta) => AccessibleFolderMeta
  ): void {
    let changed = false;
    const next = this.optimisticCreatedFolders$.value.map(folder => {
      if (folder.id !== folderId) {
        return folder;
      }
      changed = true;
      return updater(this.cloneFolderMeta(folder));
    });
    if (changed) {
      this.setOptimisticCreatedFolders(next);
    }
  }

  private readLocalTrashedDocIds(): Set<string> {
    const workspace = this.workspaceService.workspace as
      | {
          rootYDoc?: {
            getMap?: (name: string) => { get?: (key: string) => unknown };
          };
        }
      | undefined;
    const pages = workspace?.rootYDoc?.getMap?.('meta')?.get?.('pages');
    if (!pages || typeof pages !== 'object' || !('forEach' in pages)) {
      return new Set();
    }

    const result = new Set<string>();
    try {
      (
        pages as {
          forEach(callback: (value: unknown) => void): void;
        }
      ).forEach(value => {
        if (!value || typeof value !== 'object' || !('get' in value)) {
          return;
        }
        const yMap = value as { get(key: string): unknown };
        const id = yMap.get('id');
        if (typeof id === 'string' && yMap.get('trash') === true) {
          result.add(id);
        }
      });
    } catch {
      return new Set();
    }
    return result;
  }

  private filterVisibleFolderDocIds(
    docIds: string[],
    workspaceId: string
  ): string[] {
    const visibleDocIds = filterUserVisibleDocIds(docIds, workspaceId);
    const locallyTrashedDocIds = this.readLocalTrashedDocIds();
    if (locallyTrashedDocIds.size === 0) {
      return visibleDocIds;
    }
    return visibleDocIds.filter(docId => {
      if (!locallyTrashedDocIds.has(docId)) {
        return true;
      }

      // Root-Y.Doc metadata can contain stale trash state from another actor.
      // It may only override a fresh server-visible shape when this principal
      // has explicit local authority to trash the document.
      const authorityStore = this.authorityStore;
      return authorityStore
        ? authorityStore.getDocPermissions(docId)?.Doc_Trash !== true
        : false;
    });
  }

  private setRootFolderDocIds(workspaceId: string, docIds: string[]): void {
    const visibleDocIds = this.applyPendingDocAssignmentsToRootDocIds(
      this.filterVisibleFolderDocIds(docIds, workspaceId)
    );
    const currentDocIds = this.rootFolderDocIds$.value;
    if (
      currentDocIds.length === visibleDocIds.length &&
      currentDocIds.every((docId, index) => docId === visibleDocIds[index])
    ) {
      return;
    }
    this.rootFolderDocIds$.setValue(visibleDocIds);
    recordWorkspaceProjectionDiagnostic({
      stage: 'folder-cache:root-doc-ids-changed',
      docIds: visibleDocIds,
    });
    this.saveRootFolderDocIdsToIDB(visibleDocIds);
  }

  private loadOrderState(): AccessibleFolderOrderState | null {
    const key = this.orderStorageKey;
    if (!key || typeof globalThis.localStorage === 'undefined') {
      return null;
    }
    try {
      const raw = globalThis.localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<AccessibleFolderOrderState>;
      if (!Array.isArray(parsed.folderIds)) {
        return null;
      }
      return {
        folderIds: parsed.folderIds.filter(
          (id): id is string => typeof id === 'string'
        ),
        folderDocIds:
          parsed.folderDocIds && typeof parsed.folderDocIds === 'object'
            ? Object.fromEntries(
                Object.entries(parsed.folderDocIds).map(
                  ([folderId, docIds]) => [
                    folderId,
                    Array.isArray(docIds)
                      ? docIds.filter(
                          (id): id is string => typeof id === 'string'
                        )
                      : [],
                  ]
                )
              )
            : {},
      };
    } catch {
      return null;
    }
  }

  private saveOrderState(folders: AccessibleFolderMeta[]): void {
    const key = this.orderStorageKey;
    if (!key || typeof globalThis.localStorage === 'undefined') {
      return;
    }
    const state: AccessibleFolderOrderState = {
      folderIds: folders.map(folder => folder.id),
      folderDocIds: Object.fromEntries(
        folders.map(folder => [folder.id, [...folder.docIds]])
      ),
    };
    try {
      globalThis.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // localStorage unavailable/quota — order remains in memory only
    }
  }

  private applyOrderState(
    folders: AccessibleFolderMeta[]
  ): AccessibleFolderMeta[] {
    const state = this.loadOrderState();
    if (!state) {
      return folders;
    }

    const folderById = new Map(folders.map(folder => [folder.id, folder]));
    const orderedFolders: AccessibleFolderMeta[] = [];
    for (const folderId of state.folderIds) {
      const folder = folderById.get(folderId);
      if (!folder) {
        continue;
      }
      orderedFolders.push(this.applyDocOrderState(folder, state));
      folderById.delete(folderId);
    }

    for (const folder of folders) {
      if (folderById.has(folder.id)) {
        orderedFolders.push(this.applyDocOrderState(folder, state));
      }
    }

    return orderedFolders;
  }

  private applyDocOrderState(
    folder: AccessibleFolderMeta,
    state: AccessibleFolderOrderState
  ): AccessibleFolderMeta {
    const orderedDocIds = state.folderDocIds[folder.id];
    if (!orderedDocIds || orderedDocIds.length === 0) {
      return folder;
    }

    const serverDocIds = new Set(folder.docIds);
    const nextDocIds = [
      ...orderedDocIds.filter(docId => serverDocIds.has(docId)),
      ...folder.docIds.filter(docId => !orderedDocIds.includes(docId)),
    ];
    if (
      nextDocIds.length === folder.docIds.length &&
      nextDocIds.every((docId, index) => docId === folder.docIds[index])
    ) {
      return folder;
    }
    return { ...folder, docIds: nextDocIds };
  }

  private loadOptimisticTrashState(): AccessibleFolderOptimisticTrashRecord[] {
    return this.optimisticTrashedFolders$.value;
  }

  private setOptimisticTrashRecords(
    records: AccessibleFolderOptimisticTrashRecord[]
  ): void {
    const cloned = records.map(record => this.cloneTrashRecord(record));
    this.optimisticTrashedFolders$.setValue(cloned);
    this.persistOptimisticTrashState(cloned);
  }

  private optimisticTrashedFolderIds(): Set<string> {
    const records = this.loadOptimisticTrashState();
    const ids = new Set<string>();
    for (const record of records) {
      ids.add(record.id);
      for (const folder of record.folders) {
        ids.add(folder.id);
      }
    }
    return ids;
  }

  private applyOptimisticTrashFilter(
    folders: AccessibleFolderMeta[]
  ): AccessibleFolderMeta[] {
    const trashedIds = this.optimisticTrashedFolderIds();
    if (trashedIds.size === 0) {
      return folders;
    }
    return folders.filter(folder => !trashedIds.has(folder.id));
  }

  private loadPendingDocAssignments(): PendingDocFolderAssignment[] {
    return this.pendingDocFolderAssignments$.value;
  }

  private setPendingDocAssignments(
    assignments: PendingDocFolderAssignment[]
  ): void {
    const uniqueByDocId = new Map<string, PendingDocFolderAssignment>();
    for (const assignment of assignments) {
      uniqueByDocId.set(
        assignment.docId,
        this.clonePendingDocAssignment(assignment)
      );
    }
    const next = [...uniqueByDocId.values()];
    this.pendingDocFolderAssignments$.setValue(next);
    this.persistPendingDocAssignments(next);
  }

  private persistOptimisticTrashState(
    records: AccessibleFolderOptimisticTrashRecord[]
  ): void {
    const desired = new Map(records.map(record => [record.id, record]));
    this.queuePendingStateWrite(async storage => {
      const existing = storage
        .getPendingMutations()
        .filter(mutation =>
          mutation.localMutationId.startsWith('folder-trash:')
        );
      for (const mutation of existing) {
        if (!desired.has(mutation.nodeId)) {
          await storage.rejectPending(mutation.localMutationId);
        }
      }
      for (const record of desired.values()) {
        const nodes = record.folders.map(folder =>
          this.folderMetaToMetadataNode(record.workspaceId, folder)
        );
        await storage.applyLocalMutation(
          nodes,
          this.metadataPendingMutation(storage, {
            localMutationId: `folder-trash:${record.id}`,
            nodeId: record.id,
            op: 'trash',
            patch: { trashedAt: record.trashedAt },
            createdAt: record.trashedAt,
          })
        );
      }
    });
  }

  private persistPendingDocAssignments(
    assignments: PendingDocFolderAssignment[]
  ): void {
    const desired = new Map(
      assignments.map(assignment => [assignment.docId, assignment])
    );
    this.queuePendingStateWrite(async storage => {
      const existing = storage
        .getPendingMutations()
        .filter(mutation =>
          mutation.localMutationId.startsWith('folder-doc-move:')
        );
      for (const mutation of existing) {
        if (!desired.has(mutation.nodeId)) {
          await storage.rejectPending(mutation.localMutationId);
        }
      }
      for (const assignment of desired.values()) {
        await storage.enqueuePending(
          this.metadataPendingMutation(storage, {
            localMutationId: `folder-doc-move:${assignment.docId}`,
            nodeId: assignment.docId,
            op: 'move',
            patch: { parentId: assignment.folderId },
            createdAt: new Date(assignment.createdAt).toISOString(),
          })
        );
      }
    });
  }

  private queuePendingStateWrite(
    write: (storage: WorkspaceMetadataStore) => Promise<void>
  ): void {
    const storage = this.metadataStore;
    if (!storage) return;
    this.pendingStateWrite = this.pendingStateWrite
      .then(async () => {
        await storage.hydrate();
        await write(storage);
      })
      .catch(error => {
        console.error('Failed to persist local folder metadata state:', error);
      });
  }

  private metadataPendingMutation(
    storage: WorkspaceMetadataStore,
    input: Pick<
      WorkspaceMetadataPendingMutation,
      'createdAt' | 'localMutationId' | 'nodeId' | 'op' | 'patch'
    >
  ): WorkspaceMetadataPendingMutation {
    const state = storage.getShapeState();
    return {
      accountId: state.accountId,
      principalId: state.principalId,
      serverId: state.serverId,
      serverBaseUrl: state.serverBaseUrl,
      visibilityMode: state.visibilityMode,
      tenantId: state.tenantId,
      shapeKey: state.shapeKey,
      schemaVersion: state.schemaVersion,
      permissionGraphVersion: state.permissionGraphVersion,
      workspaceId: state.workspaceId,
      localMutationId: input.localMutationId,
      nodeId: input.nodeId,
      op: input.op,
      baseRevision: null,
      patch: input.patch,
      status: 'queued',
      createdAt: input.createdAt,
    };
  }

  private applyPendingDocAssignmentsToRootDocIds(docIds: string[]): string[] {
    const assignments = this.loadPendingDocAssignments();
    if (assignments.length === 0) {
      return docIds;
    }
    const rootFolderId = this.cache$.value.find(
      folder => folder.isWorkspaceRoot
    )?.id;
    const pendingMovesAwayFromRoot = new Set(
      assignments
        .filter(assignment => assignment.folderId !== rootFolderId)
        .map(assignment => assignment.docId)
    );
    const visibleDocIds = docIds.filter(
      docId => !pendingMovesAwayFromRoot.has(docId)
    );
    if (!rootFolderId) {
      return visibleDocIds;
    }
    const pendingMovesToRoot = assignments
      .filter(assignment => assignment.folderId === rootFolderId)
      .map(assignment => assignment.docId);
    return [
      ...pendingMovesToRoot.filter(docId => !visibleDocIds.includes(docId)),
      ...visibleDocIds,
    ];
  }

  private applyPendingDocAssignments(
    folders: AccessibleFolderMeta[],
    authoritativeFolderIds: ReadonlySet<string> = new Set()
  ): AccessibleFolderMeta[] {
    let assignments = this.loadPendingDocAssignments();
    if (assignments.length === 0) {
      return folders;
    }

    const folderById = new Map(folders.map(folder => [folder.id, folder]));
    const materializedDocIds = new Set<string>();
    for (const assignment of assignments) {
      if (!authoritativeFolderIds.has(assignment.folderId)) {
        continue;
      }
      const targetContainsDoc = folderById
        .get(assignment.folderId)
        ?.docIds.includes(assignment.docId);
      const staleSourceStillContainsDoc = folders.some(
        folder =>
          folder.id !== assignment.folderId &&
          folder.docIds.includes(assignment.docId)
      );
      if (targetContainsDoc && !staleSourceStillContainsDoc) {
        materializedDocIds.add(assignment.docId);
      }
    }
    if (materializedDocIds.size > 0) {
      assignments = assignments.filter(
        assignment => !materializedDocIds.has(assignment.docId)
      );
      this.setPendingDocAssignments(assignments);
    }
    if (assignments.length === 0) {
      return folders;
    }

    const assignmentByDocId = new Map(
      assignments.map(assignment => [assignment.docId, assignment])
    );
    const assignmentsByFolderId = new Map<
      string,
      PendingDocFolderAssignment[]
    >();
    for (const assignment of assignments) {
      const folderAssignments =
        assignmentsByFolderId.get(assignment.folderId) ?? [];
      folderAssignments.push(assignment);
      assignmentsByFolderId.set(assignment.folderId, folderAssignments);
    }

    let changed = false;
    const next = folders.map(folder => {
      let docIds = folder.docIds.filter(docId => {
        const assignment = assignmentByDocId.get(docId);
        return !assignment || assignment.folderId === folder.id;
      });

      for (const assignment of assignmentsByFolderId.get(folder.id) ?? []) {
        if (!docIds.includes(assignment.docId)) {
          docIds = [assignment.docId, ...docIds];
        }
      }

      if (
        docIds.length !== folder.docIds.length ||
        docIds.some((docId, index) => docId !== folder.docIds[index])
      ) {
        changed = true;
        return { ...folder, docIds };
      }
      return folder;
    });

    return changed ? next : folders;
  }

  // ─── Local metadata store ───────────────────────────────────────────

  private async loadFromIDB(): Promise<void> {
    if (this.idbLoaded) return;
    const startedAt = performance.now();
    recordBootEvent('folder-cache-hydration-started');
    try {
      const permissionStartedAt = performance.now();
      await this.authorityStore?.hydrateLocal();
      recordBootEvent('folder-cache-permission-ready', {
        durationMs: performance.now() - permissionStartedAt,
      });
      this.observePermissionGraphVersion(
        this.authorityStore?.getPermissionGraphVersion() ?? 0
      );
      const storage = this.metadataStore;
      const wsId = this.workspaceId;
      if (!storage || !wsId) return;

      this.idbLoaded = true;
      try {
        await storage.hydrate();
        for (const [key, cursor] of Object.entries(
          storage.getShapeState().pageCursors
        )) {
          if (!key.startsWith('children:')) continue;
          const folderId = key.slice('children:'.length);
          this.folderChildrenNextCursor.set(folderId, cursor);
          this.loadedFolderChildren.add(folderId);
        }
        this.hydratePendingStateFromMetadataStore(storage);
        const freshCached = storage
          .getVisibleNodes()
          .filter(node => node.kind === 'folder' && node.folder);
        if (freshCached.length > 0 && this.cache$.value.length === 0) {
          this.permissionGraphVersion = mergePermissionGraphVersion(
            this.permissionGraphVersion,
            storage.getShapeState().permissionGraphVersion ?? 0
          );
          const cachedFolders = this.applyPendingDocAssignments(
            this.applyOptimisticTrashFilter(
              this.applyOrderState(
                freshCached
                  .map(node => this.metadataNodeToMeta(node))
                  .filter(folder => !isProjectionSystemContainer(folder))
              )
            )
          );
          this.cache$.setValue(cachedFolders);
          const root = cachedFolders.find(folder => folder.isWorkspaceRoot);
          if (root && this.rootFolderDocIds$.value.length === 0) {
            this.rootFolderDocIds$.setValue(
              this.applyPendingDocAssignmentsToRootDocIds(
                filterUserVisibleDocIds(root.docIds, wsId)
              )
            );
          }
          this.isPopulated$.setValue(true);
        }
      } catch {
        // IDB unavailable — fall through to server fetch
      }
    } finally {
      // A missing account/tenant scope means the account-scoped IDB was not
      // checked at all. Keep readiness blocked until local authority hydration
      // or an online permission snapshot makes the correct store addressable.
      if (this.idbLoaded) {
        this.isLocalCacheLoaded$.setValue(true);
      }
      recordBootEvent('folder-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        loaded: this.idbLoaded,
        nodeCount: this.cache$.value.length,
        populated: this.isPopulated$.value,
      });
    }
  }

  /**
   * A child window is materialized only while its `children:<folderId>` cursor
   * is stored. A forced re-read drops that cursor first, so a read the page
   * never finished (navigation, reload) is read again instead of leaving the
   * older shape standing.
   */
  private async forgetStoredChildWindows(folderIds: Iterable<string>) {
    await this.reconcileMetadata(async () => {
      const storage = this.metadataStore;
      if (!storage) return;
      const pageCursors = { ...storage.getShapeState().pageCursors };
      let changed = false;
      for (const folderId of folderIds) {
        const key = `children:${folderId}`;
        if (key in pageCursors) {
          delete pageCursors[key];
          changed = true;
        }
      }
      if (changed) await storage.updateShapeState({ pageCursors });
    });
  }

  private async pendingMetadataNodeIds(): Promise<ReadonlySet<string>> {
    await this.pendingStateWrite;
    return new Set(
      this.metadataStore?.getPendingMutations().map(mutation => mutation.nodeId)
    );
  }

  private hydratePendingStateFromMetadataStore(
    storage: WorkspaceMetadataStore
  ): void {
    const pending = storage.getPendingMutations();
    const staleMutationIds = new Set<string>();
    const assignments = pending
      .filter(
        mutation =>
          mutation.op === 'move' &&
          mutation.localMutationId.startsWith('folder-doc-move:') &&
          typeof mutation.patch.parentId === 'string'
      )
      .map(mutation => ({
        docId: mutation.nodeId,
        folderId: mutation.patch.parentId as string,
        createdAt: Date.parse(mutation.createdAt),
      }));
    this.pendingDocFolderAssignments$.setValue(assignments);

    const canonicalFolders = storage
      .getCanonicalNodes()
      .filter(
        (
          node
        ): node is WorkspaceListMetadata & {
          folder: NonNullable<WorkspaceListMetadata['folder']>;
        } => node.kind === 'folder' && !!node.folder
      )
      .map(node => this.metadataNodeToMeta(node));
    const folderById = new Map(
      canonicalFolders.map(folder => [folder.id, folder])
    );
    const records: AccessibleFolderOptimisticTrashRecord[] = [];
    for (const mutation of pending) {
      if (
        mutation.op !== 'trash' ||
        !mutation.localMutationId.startsWith('folder-trash:')
      ) {
        continue;
      }
      const root = folderById.get(mutation.nodeId);
      const trashedAt =
        typeof mutation.patch.trashedAt === 'string'
          ? mutation.patch.trashedAt
          : mutation.createdAt;
      if (!root) {
        staleMutationIds.add(mutation.localMutationId);
        continue;
      }
      const subtreeIds = new Set([root.id]);
      let changed = true;
      while (changed) {
        changed = false;
        for (const folder of canonicalFolders) {
          if (
            folder.parentId &&
            subtreeIds.has(folder.parentId) &&
            !subtreeIds.has(folder.id)
          ) {
            subtreeIds.add(folder.id);
            changed = true;
          }
        }
      }
      const folders = canonicalFolders
        .filter(folder => subtreeIds.has(folder.id))
        .map(folder => this.cloneFolderMeta(folder));
      records.push({
        id: root.id,
        workspaceId: this.workspaceId ?? storage.getShapeState().workspaceId,
        parentId: root.parentId,
        name: root.name,
        docIds: [...new Set(folders.flatMap(folder => folder.docIds))],
        folders,
        trashedAt,
      });
    }
    this.optimisticTrashedFolders$.setValue(records);
    if (staleMutationIds.size > 0) {
      this.queuePendingStateWrite(async currentStorage => {
        for (const mutationId of staleMutationIds) {
          await currentStorage.rejectPending(mutationId);
        }
      });
    }
  }

  private metadataNodeToMeta(
    node: WorkspaceListMetadata
  ): AccessibleFolderMeta {
    const folder = node.folder!;
    const docIds = node.docIds ?? [];
    const workspaceId = this.workspaceId;
    return {
      id: node.nodeId,
      name: node.title,
      parentId: node.parentId,
      ownerUserId: folder.ownerUserId,
      systemKind: folder.systemKind,
      visibility: folder.visibility,
      inheritPermissions: folder.inheritPermissions,
      isWorkspaceRoot: folder.isWorkspaceRoot,
      immutable: folder.immutable,
      storageLimitBytes: folder.storageLimitBytes,
      storageUsedBytes: folder.storageUsedBytes,
      metadataOnly: folder.metadataOnly,
      // A child-shape read may materialize Document links before the
      // corresponding Document metadata record is ready for the sidebar.
      // Never let an older negative summary hint override those canonical
      // links, otherwise a populated Folder briefly renders as empty.
      hasChildren: node.hasChildren || docIds.length > 0,
      docIds: workspaceId
        ? this.filterVisibleFolderDocIds(docIds, workspaceId)
        : docIds,
      permissionGraphVersion: this.permissionGraphVersion,
      revision: node.subtreeRevision,
    };
  }

  private folderMetaToMetadataNode(
    workspaceId: string,
    folder: AccessibleFolderMeta
  ): WorkspaceListMetadata {
    return {
      workspaceId,
      nodeId: folder.id,
      kind: 'folder',
      title: folder.name,
      parentId: folder.parentId,
      areaId: null,
      sortKey: folder.id,
      hasChildren: folder.hasChildren ?? true,
      childCount: null,
      subtreeRevision: folder.revision ?? null,
      trashedAt: null,
      updatedAt: folder.revision ?? UNKNOWN_SERVER_REVISION,
      permissionHint: folder.metadataOnly ? 'unknown' : 'read',
      syncState: 'fresh',
      docIds: [...folder.docIds],
      folder: {
        ownerUserId: folder.ownerUserId,
        systemKind: folder.systemKind ?? null,
        visibility: folder.visibility,
        inheritPermissions: folder.inheritPermissions,
        isWorkspaceRoot: folder.isWorkspaceRoot,
        immutable: folder.immutable,
        storageLimitBytes: folder.storageLimitBytes,
        storageUsedBytes: folder.storageUsedBytes,
        metadataOnly: folder.metadataOnly,
      },
    };
  }

  // ─── Server Fetch ───────────────────────────────────────────────────

  private ensureFetched(): void {
    if (this.fetchTriggered || this.disposed) return;
    this.fetchTriggered = true;

    this.startReconnectListener();
    this.startBackendAvailabilityListener();
    this.startVisibilityListener();

    this.loadFromIDB()
      .then(() => this.fetchAccessibleFolders())
      .then(() => {
        if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
          this.scheduleRetry(0);
        }
      })
      .catch(() => {
        this.scheduleRetry(0);
      });
  }

  prime(): void {
    this.ensureFetched();
  }

  private static readonly RETRY_DELAYS_MS = [500, 1000, 2000, 5000];

  private scheduleRetry(attempt: number): void {
    if (this.retryTimer || this.disposed) return;
    if (attempt >= AccessibleFolderCacheStore.RETRY_DELAYS_MS.length) {
      return;
    }
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.disposed) return;
      this.loadFromIDB()
        .then(() => this.fetchAccessibleFolders())
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(attempt + 1);
          }
        })
        .catch(() => {
          this.scheduleRetry(attempt + 1);
        });
    }, AccessibleFolderCacheStore.RETRY_DELAYS_MS[attempt]);
  }

  async fetchAccessibleFolders(signal?: AbortSignal): Promise<void> {
    if (this.disposed) return;
    if (!signal && this.fetchAccessibleFoldersPromise) {
      // A realtime invalidation may arrive while an older snapshot/delta is in
      // flight. Coalescing it into that request can lose the newer change, so
      // always run one follow-up pass against the token returned by the first.
      this.fetchAccessibleFoldersAgain = true;
      return this.fetchAccessibleFoldersPromise;
    }
    const fetchPromise = signal
      ? this.fetchAccessibleFoldersFromServer(signal)
      : (async () => {
          do {
            this.fetchAccessibleFoldersAgain = false;
            await this.fetchAccessibleFoldersFromServer(
              this.lifecycleAbortController.signal
            );
          } while (this.fetchAccessibleFoldersAgain && !this.disposed);
        })();
    if (!signal) {
      const trackedPromise = fetchPromise.finally(() => {
        if (this.fetchAccessibleFoldersPromise === trackedPromise) {
          this.fetchAccessibleFoldersPromise = null;
        }
      });
      this.fetchAccessibleFoldersPromise = trackedPromise;
      return trackedPromise;
    }
    return fetchPromise;
  }

  async hydrateLocalCache(): Promise<void> {
    await this.loadFromIDB();
    await this.pendingStateWrite;
  }

  async fetchFolderChildren(
    folderId: string,
    options: {
      force?: boolean;
      loadMore?: boolean;
      signal?: AbortSignal;
      diagnosticReason?: string;
      reuseRecent?: boolean;
    } = {}
  ): Promise<void> {
    if (this.disposed) return;
    const workspaceId = this.workspaceId;
    if (!workspaceId || isBackendKnownUnavailable()) return;
    const after = options.loadMore
      ? this.folderChildrenNextCursor.get(folderId)
      : null;
    if (options.loadMore && !after) return;
    if (
      !options.force &&
      !options.loadMore &&
      this.loadedFolderChildren.has(folderId)
    ) {
      return;
    }

    const initialLoad =
      !options.loadMore && !this.loadedFolderChildren.has(folderId);
    if (initialLoad) this.markInitialChildrenLoading(folderId, true);
    try {
      await this.readFolderChildren(
        workspaceId,
        folderId,
        after ?? null,
        options
      );
    } finally {
      if (initialLoad) this.markInitialChildrenLoading(folderId, false);
    }
  }

  private markInitialChildrenLoading(folderId: string, loading: boolean) {
    const current = this.initialChildrenLoadingFolderIds$.value;
    if (current.has(folderId) === loading) return;
    const next = new Set(current);
    if (loading) next.add(folderId);
    else next.delete(folderId);
    this.initialChildrenLoadingFolderIds$.setValue(next);
  }

  private async readFolderChildren(
    workspaceId: string,
    folderId: string,
    after: string | null,
    options: {
      loadMore?: boolean;
      signal?: AbortSignal;
      diagnosticReason?: string;
      reuseRecent?: boolean;
    }
  ): Promise<void> {
    const query = new URLSearchParams({ first: '100' });
    if (after) query.set('after', after);
    const signal = options.signal ?? this.lifecycleAbortController.signal;
    recordWorkspaceProjectionDiagnostic({
      stage: 'folder-cache:children-request-started',
      reason: options.diagnosticReason ?? 'unspecified',
      folderIds: [folderId],
    });
    let readAttempt = 0;
    const page = await readPermissionGraphSnapshotWithCatchUp({
      read: () =>
        this.fetchFolderChildrenPage(
          workspaceId,
          folderId,
          query,
          signal,
          options.reuseRecent ? readAttempt++ : undefined
        ),
      getIncomingVersion: value => value.permissionGraphVersion,
      getCurrentVersion: () => this.permissionGraphVersion,
      signal,
    });
    if (!page) return;
    await this.reconcileMetadata(async () => {
      this.observePermissionGraphVersion(page.permissionGraphVersion);

      const folders = this.folderItemsFromPage(page, folderId);
      const incomingParent = page.parent?.folder
        ? this.folderItemFromMetadata(page.parent, page.permissionGraphVersion)
        : null;
      const docIds = this.filterVisibleFolderDocIds(
        page.items.filter(item => item.type === 'doc').map(item => item.id),
        workspaceId
      );
      const incomingById = new Map(folders.map(folder => [folder.id, folder]));
      const optimisticIds = new Set(
        this.optimisticCreatedFolders$.value.map(folder => folder.id)
      );
      const pendingNodeIds = await this.pendingMetadataNodeIds();
      const currentShape = options.loadMore
        ? this.cache$.value
        : this.cache$.value.filter(
            folder =>
              folder.parentId !== folderId ||
              incomingById.has(folder.id) ||
              optimisticIds.has(folder.id)
          );
      let parentFound = false;
      const merged = currentShape.map(folder => {
        if (folder.id === folderId) {
          parentFound = true;
          return {
            ...(incomingParent ?? folder),
            docIds: options.loadMore
              ? Array.from(new Set([...folder.docIds, ...docIds]))
              : docIds,
          };
        }
        const incoming = incomingById.get(folder.id);
        // A parent window carries metadata for its direct child Folders, but
        // never the Documents inside those child Folders. Preserve the
        // already materialized child shape until that Folder's own children
        // window replaces it authoritatively.
        return incoming ? { ...incoming, docIds: folder.docIds } : folder;
      });
      for (const folder of folders) {
        if (!merged.some(existing => existing.id === folder.id)) {
          merged.push(folder);
        }
      }
      if (incomingParent && !parentFound) {
        merged.push({
          ...incomingParent,
          docIds: options.loadMore
            ? Array.from(new Set([...incomingParent.docIds, ...docIds]))
            : docIds,
        });
        parentFound = true;
      }
      const mergedParent = parentFound
        ? merged.find(folder => folder.id === folderId)
        : undefined;
      if (mergedParent?.isWorkspaceRoot) {
        this.setRootFolderDocIds(workspaceId, mergedParent.docIds);
      }
      const ordered = this.applyPendingDocAssignments(
        this.applyOrderState(merged),
        new Set([folderId])
      );
      this.folderChildrenNextCursor.set(
        folderId,
        page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null
      );
      this.loadedFolderChildren.add(folderId);
      // A complete first window is authoritative for the direct children.
      const removedChildIds =
        options.loadMore || page.pageInfo.hasNextPage
          ? []
          : this.cache$.value
              .filter(
                folder =>
                  folder.parentId === folderId &&
                  !incomingById.has(folder.id) &&
                  !optimisticIds.has(folder.id) &&
                  !pendingNodeIds.has(folder.id)
              )
              .map(folder => folder.id);
      const removedSubtreeIds = this.folderSubtreeIds(removedChildIds);
      const reconciled = await this.syncToIDB(
        workspaceId,
        ordered.filter(folder => !removedSubtreeIds.has(folder.id)),
        {
          key: `children:${folderId}`,
          cursor: this.folderChildrenNextCursor.get(folderId) ?? null,
        },
        [...removedSubtreeIds]
      );
      const authoritativeParent = ordered.find(
        folder => folder.id === folderId
      );
      // The child-window response is authoritative for this parent's direct
      // links. An equal-revision IDB row may still carry the older empty
      // docIds shape, because document placement can change without changing
      // the folder metadata revision. Never let that persisted summary erase
      // the freshly read child window before the sidebar observes it.
      const reconciledWithAuthoritativeWindow = authoritativeParent
        ? reconciled.map(folder =>
            folder.id === folderId
              ? {
                  ...folder,
                  docIds: [...authoritativeParent.docIds],
                  hasChildren:
                    authoritativeParent.hasChildren === true ||
                    authoritativeParent.docIds.length > 0,
                }
              : folder
          )
        : reconciled;
      // Local creation can run while syncToIDB awaits persistence. Reapply
      // the live assignment overlay, not the one captured before that await.
      this.cache$.setValue(
        this.applyPendingDocAssignments(reconciledWithAuthoritativeWindow)
      );
      recordWorkspaceProjectionDiagnostic({
        stage: 'folder-cache:children-request-applied',
        reason: options.diagnosticReason ?? 'unspecified',
        folderIds: [folderId],
        docIds,
      });
    });
  }

  private async fetchFolderChildrenPage(
    workspaceId: string,
    folderId: string | null,
    query: URLSearchParams,
    signal?: AbortSignal,
    reuseAttempt?: number
  ): Promise<WorkspaceFolderChildrenPage> {
    const segment = folderId ? encodeURIComponent(folderId) : 'root';
    const load = () =>
      defaultWorkspaceApiClient.requestJson<
        WorkspaceFolderChildrenPage & {
          permissionSnapshots?: PermissionSnapshotResponse[];
        }
      >({
        workspaceId,
        category: 'metadata',
        key: `folder-children:${folderId ?? 'root'}:${query.get('after') ?? 'first'}`,
        mode: 'background',
        priority: folderId ? 'visible' : 'background',
        path: `/api/workspaces/${workspaceId}/metadata/folders/${segment}/children?${query}`,
        init: { signal },
      });
    const page =
      reuseAttempt === undefined
        ? await load()
        : await readWorkspaceCached({
            workspaceId,
            key:
              `folder-children:${folderId ?? 'root'}:` +
              `${query.get('after') ?? 'first'}:` +
              `scope:${this.folderChildrenReadScope}:` +
              `generation:${this.folderChildrenReadGeneration}:` +
              `attempt:${reuseAttempt}`,
            signal,
            ttlMs: FOLDER_CHILDREN_COMPLETED_REUSE_MS,
            load,
          });
    if (page.permissionSnapshots) {
      await this.authorityStore?.applyOnlineSnapshots({
        snapshots: page.permissionSnapshots,
      });
    }
    return page;
  }

  private folderItemsFromPage(
    page: WorkspaceMetadataPage,
    parentId: string | null
  ): AccessibleFolderMeta[] {
    this.observePermissionGraphVersion(page.permissionGraphVersion);
    return page.items
      .filter(item => item.type === 'folder' && item.folder)
      .map(item =>
        this.folderItemFromMetadata(
          { ...item, parentId: item.parentId ?? parentId },
          page.permissionGraphVersion
        )
      );
  }

  private folderItemFromMetadata(
    item: WorkspaceMetadataPage['items'][number],
    permissionGraphVersion: number
  ): AccessibleFolderMeta {
    const folder = item.folder!;
    return {
      id: item.id,
      name: item.title,
      parentId: item.parentId ?? null,
      ownerUserId: folder.ownerUserId,
      systemKind: folder.systemKind,
      visibility: folder.visibility,
      inheritPermissions: folder.inheritPermissions,
      isWorkspaceRoot: folder.isWorkspaceRoot,
      immutable: folder.immutable,
      storageLimitBytes: folder.storageLimitBytes,
      storageUsedBytes: folder.storageUsedBytes,
      metadataOnly: folder.metadataOnly,
      hasChildren: item.hasChildren,
      docIds: [],
      permissionGraphVersion,
      revision: item.revision ?? item.updatedAt,
    };
  }

  private mergeRootWindowWithCachedDescendants(
    serverFolders: AccessibleFolderMeta[]
  ): AccessibleFolderMeta[] {
    const currentFolders = this.cache$.value;
    if (currentFolders.length === 0) {
      return serverFolders;
    }

    const incomingById = new Map(
      serverFolders.map(folder => [folder.id, folder])
    );
    const currentById = new Map(
      currentFolders.map(folder => [folder.id, folder])
    );
    const workspaceRootId =
      serverFolders.find(folder => folder.isWorkspaceRoot)?.id ??
      currentFolders.find(folder => folder.isWorkspaceRoot)?.id ??
      null;
    if (workspaceRootId === null) {
      return serverFolders;
    }
    const belongsToRootWindow = (folder: AccessibleFolderMeta) =>
      folder.isWorkspaceRoot ||
      folder.parentId === null ||
      folder.systemKind === 'private_root' ||
      folder.parentId === workspaceRootId;

    const retainedDescendants = currentFolders.filter(folder => {
      if (incomingById.has(folder.id) || belongsToRootWindow(folder)) {
        return false;
      }

      const visited = new Set([folder.id]);
      let parentId = folder.parentId;
      while (parentId) {
        if (incomingById.has(parentId)) {
          return true;
        }
        if (visited.has(parentId)) {
          return false;
        }
        visited.add(parentId);
        const parent = currentById.get(parentId);
        if (!parent || belongsToRootWindow(parent)) {
          return false;
        }
        parentId = parent.parentId;
      }
      return false;
    });

    return this.applyOrderState([...serverFolders, ...retainedDescendants]);
  }

  private async fetchAccessibleFoldersFromServer(
    signal?: AbortSignal
  ): Promise<void> {
    await this.loadFromIDB();
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('Server not available');
    }

    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;

    const deltaToken =
      this.metadataDeltaToken ??
      this.metadataStore?.getShapeState().token ??
      null;
    if (deltaToken && this.cache$.value.length > 0) {
      await this.fetchFolderDeltas(workspace.id, deltaToken, signal);
      return;
    }

    // Capture the delta head before the root snapshot. A mutation after this
    // point is then guaranteed to remain visible to the next delta request.
    // Capturing the head after the snapshot could advance past a concurrent
    // rename and leave the cached title stale until its folder was opened.
    const snapshotDeltaToken = await this.fetchMetadataDeltaHeadToken(
      workspace.id,
      signal
    );

    let rootPage: WorkspaceMetadataPage;
    try {
      rootPage = await this.fetchFolderChildrenPage(
        workspace.id,
        null,
        new URLSearchParams({ first: '100' }),
        signal
      );
      this.authoritativeAccessDenied = false;
    } catch (error) {
      if (this.failClosedForAuthoritativeAccessError(error)) {
        return;
      }
      throw error;
    }

    // The response may have supplied the first verified tenant scope. Hydrate
    // that account-scoped local projection before publishing server changes.
    await this.loadFromIDB();

    let appliedSnapshot = false;
    await this.reconcileMetadata(async () => {
      let serverFolders = this.folderItemsFromPage(rootPage, null);
      const workspaceRoot = serverFolders.find(
        folder => folder.isWorkspaceRoot
      );
      if (workspaceRoot) {
        const rootDocIds = this.filterVisibleFolderDocIds(
          rootPage.items
            .filter(
              item => item.type === 'doc' && item.parentId === workspaceRoot.id
            )
            .map(item => item.id),
          workspace.id
        );
        serverFolders = serverFolders.map(folder =>
          folder.id === workspaceRoot.id
            ? { ...folder, docIds: rootDocIds }
            : folder
        );
        this.setRootFolderDocIds(workspace.id, rootDocIds);
        this.folderChildrenNextCursor.set(
          workspaceRoot.id,
          rootPage.pageInfo.hasNextPage ? rootPage.pageInfo.endCursor : null
        );
        this.loadedFolderChildren.add(workspaceRoot.id);
      }
      serverFolders = this.applyOptimisticTrashFilter(
        this.applyOrderState(
          serverFolders.filter(folder => !isProjectionSystemContainer(folder))
        )
      );
      const currentById = new Map(
        this.cache$.value.map(folder => [folder.id, folder])
      );
      serverFolders = serverFolders.map(folder => {
        const current = currentById.get(folder.id);
        return current && !folder.isWorkspaceRoot
          ? { ...folder, docIds: current.docIds }
          : folder;
      });
      serverFolders = this.mergeRootWindowWithCachedDescendants(serverFolders);
      serverFolders = this.applyPendingDocAssignments(
        serverFolders,
        workspaceRoot ? new Set([workspaceRoot.id]) : new Set()
      );
      const serverPermissionGraphVersion =
        getPermissionGraphSnapshotVersion(serverFolders);
      if (
        shouldIgnorePermissionGraphSnapshot(
          serverPermissionGraphVersion,
          this.permissionGraphVersion
        )
      ) {
        return;
      }
      this.permissionGraphVersion = mergePermissionGraphVersion(
        this.permissionGraphVersion,
        serverPermissionGraphVersion
      );
      serverFolders = this.mergeOptimisticCreatedFolders(serverFolders);
      // A complete root window is authoritative for the folders directly
      // under the workspace root; an optimistically trashed one stays local.
      const keptIds = new Set(serverFolders.map(folder => folder.id));
      const optimisticTrashedIds = this.optimisticTrashedFolderIds();
      const pendingNodeIds = await this.pendingMetadataNodeIds();
      const removedRootChildIds =
        workspaceRoot && !rootPage.pageInfo.hasNextPage
          ? this.cache$.value
              .filter(
                folder =>
                  folder.parentId === workspaceRoot.id &&
                  !keptIds.has(folder.id) &&
                  !optimisticTrashedIds.has(folder.id) &&
                  !pendingNodeIds.has(folder.id)
              )
              .map(folder => folder.id)
          : [];
      const removedSubtreeIds = this.folderSubtreeIds(removedRootChildIds);
      serverFolders = await this.syncToIDB(
        workspace.id,
        serverFolders.filter(folder => !removedSubtreeIds.has(folder.id)),
        workspaceRoot
          ? {
              key: `children:${workspaceRoot.id}`,
              cursor:
                this.folderChildrenNextCursor.get(workspaceRoot.id) ?? null,
            }
          : undefined,
        [...removedSubtreeIds]
      );
      this.cache$.setValue(serverFolders);
      this.isPopulated$.setValue(true);
      this.isLocalCacheLoaded$.setValue(true);
      this.serverFetchCompleted = true;
      this.isServerConfirmed$.setValue(true);
      appliedSnapshot = true;
    });
    if (appliedSnapshot) {
      this.metadataDeltaToken = snapshotDeltaToken;
      await this.metadataStore?.updateShapeState({
        token: snapshotDeltaToken,
      });
    }
  }

  private async fetchFolderDeltas(
    workspaceId: string,
    initialToken: string,
    signal?: AbortSignal
  ): Promise<void> {
    let token = initialToken;
    for (let pageIndex = 0; pageIndex < 20; pageIndex++) {
      const page = await fetchWorkspaceMetadataDeltaPage(workspaceId, token, {
        first: 100,
        priority: 'background',
        signal,
      });
      if (page.permissionSnapshots) {
        await this.authorityStore?.applyOnlineSnapshots({
          snapshots: page.permissionSnapshots,
        });
      }
      if (page.resetRequired) {
        await this.resetAuthoritativeProjection(page.permissionGraphVersion);
        await this.fetchAccessibleFoldersFromServer(signal);
        if (!this.disposed && !signal?.aborted) {
          this.completedAuthoritativeProjectionVersion = Math.max(
            this.completedAuthoritativeProjectionVersion,
            page.permissionGraphVersion
          );
        }
        return;
      }
      const applied = await this.applyFolderDeltaPage(workspaceId, page);
      // An authority event can overtake the network read or its local write.
      // Keep the previous token so a subsequent read still sees those changes.
      if (
        !applied ||
        page.permissionGraphVersion !== this.permissionGraphVersion
      )
        return;
      const storage = this.metadataStore;
      const nextToken = page.nextToken ?? token;
      // Capture the store before awaiting: its immutable scope belongs to this
      // page, even if another authority generation arrives during persistence.
      await storage?.updateShapeState({
        token: nextToken,
        permissionGraphVersion: page.permissionGraphVersion,
      });
      if (page.permissionGraphVersion !== this.permissionGraphVersion) return;
      token = nextToken;
      this.metadataDeltaToken = token;
      if (!page.hasMore) break;
    }
    this.serverFetchCompleted = true;
    this.isServerConfirmed$.setValue(true);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
  }

  private async applyFolderDeltaPage(
    workspaceId: string,
    page: WorkspaceMetadataDeltaPage
  ): Promise<boolean> {
    return this.reconcileMetadata(async () => {
      // Delta envelopes carry an actual generation, including the initial 0.
      // Unlike optional list snapshots, 0 must not bypass the stale-read fence.
      if (page.permissionGraphVersion < this.permissionGraphVersion) {
        return false;
      }
      this.observePermissionGraphVersion(page.permissionGraphVersion);
      this.authorityStore?.observePermissionGraphVersion(
        page.permissionGraphVersion
      );
      // A delta reports a trashed resource with its new state; it leaves the
      // live projection like a resource the delta no longer returns.
      const liveItems = page.items.filter(
        item => !item.trashed && !item.parentTrashed
      );
      const pageShape: WorkspaceMetadataPage = {
        items: liveItems,
        pageInfo: { hasNextPage: false, endCursor: null },
        workspaceRevision: page.workspaceRevision,
        permissionGraphVersion: page.permissionGraphVersion,
      };
      const incoming = this.folderItemsFromPage(pageShape, null);
      const incomingById = new Map(incoming.map(folder => [folder.id, folder]));
      const changedFolderIds = new Set(
        page.changes
          .filter(
            change => change.resourceType === 'folder' && change.resourceId
          )
          .map(change => change.resourceId as string)
      );
      const placementChangingDocIds = new Set(
        page.changes
          .filter(
            change =>
              change.resourceType === 'doc' &&
              change.resourceId &&
              change.changeType !== 'doc_created' &&
              change.changeType !== 'doc_renamed'
          )
          .map(change => change.resourceId as string)
      );
      const incomingDocParents = new Map(
        liveItems
          .filter(item => item.type === 'doc')
          .map(item => [item.id, item.parentId ?? null] as const)
      );
      const mergeDocumentPlacements = (
        folder: AccessibleFolderMeta,
        currentDocIds = folder.docIds
      ): AccessibleFolderMeta => {
        // A recipient-filtered doc_created permission event may have already
        // inserted the authoritative folder placement while an older folder
        // delta request is still in flight. A create/rename delta can omit the
        // document item, so treating every changed doc as a placement reset
        // would erase that newer realtime placement. Only lifecycle changes
        // that can actually move/remove a document clear its old placement.
        const docIds = currentDocIds.filter(
          docId => !placementChangingDocIds.has(docId)
        );
        for (const [docId, parentId] of incomingDocParents) {
          if (parentId === folder.id && !docIds.includes(docId)) {
            docIds.push(docId);
          }
        }
        return docIds.length === folder.docIds.length &&
          docIds.every((docId, index) => docId === folder.docIds[index])
          ? folder
          : { ...folder, docIds };
      };
      const next = this.cache$.value
        .filter(
          folder =>
            !changedFolderIds.has(folder.id) || incomingById.has(folder.id)
        )
        .map(folder => {
          const mergedFolder = incomingById.get(folder.id) ?? folder;
          return mergeDocumentPlacements(mergedFolder, folder.docIds);
        });
      for (const folder of incoming) {
        if (!next.some(existing => existing.id === folder.id))
          next.push(mergeDocumentPlacements(folder));
      }
      const merged = this.mergeOptimisticCreatedFolders(
        this.applyPendingDocAssignments(
          this.applyOptimisticTrashFilter(this.applyOrderState(next)),
          new Set(
            [...incomingDocParents.values()].filter(
              (parentId): parentId is string => parentId !== null
            )
          )
        )
      );
      // Realtime reconciliation is user-visible state. Publish the safe merged
      // in-memory projection before durable IndexedDB persistence so a slow
      // local write cannot add hundreds of milliseconds to cross-client paint.
      this.cache$.setValue(merged);
      // A changed folder the delta no longer returns is out of sight.
      const reconciled = await this.syncToIDB(
        workspaceId,
        merged,
        undefined,
        [...changedFolderIds].filter(id => !incomingById.has(id))
      );
      if (page.permissionGraphVersion !== this.permissionGraphVersion)
        return false;
      const current = this.applyPendingDocAssignments(reconciled);
      this.cache$.setValue(current);
      const root = current.find(folder => folder.isWorkspaceRoot);
      if (root) this.setRootFolderDocIds(workspaceId, root.docIds);
      return true;
    });
  }

  private async fetchMetadataDeltaHeadToken(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<string | null> {
    try {
      return await fetchWorkspaceMetadataDeltaHeadToken(workspaceId, signal);
    } catch {
      return null;
    }
  }

  private failClosedForAuthoritativeAccessError(error: unknown): boolean {
    if (!isBackendAuthOrPermissionError(error) || isBackendKnownUnavailable()) {
      return false;
    }

    this.cache$.setValue([]);
    this.rootFolderDocIds$.setValue([]);
    this.permissionGraphVersion = 0;
    this.completedAuthoritativeProjectionVersion = 0;
    this.authoritativeAccessDenied = true;
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isServerConfirmed$.setValue(false);

    void this.metadataStore?.clear().catch(() => {
      // Local cache cleanup failure must not resurrect in-memory access.
    });
    return true;
  }

  private saveRootFolderDocIdsToIDB(docIds: string[]): void {
    const root = this.cache$.value.find(folder => folder.isWorkspaceRoot);
    if (!root) return;
    this.saveLocalFolderStateToIDB([{ ...root, docIds: [...docIds] }]);
  }

  private saveLocalFolderStateToIDB(folders: AccessibleFolderMeta[]): void {
    this.queuePendingStateWrite(storage =>
      storage.applyLocalNodeDocIds(
        folders.map(folder => ({
          nodeId: folder.id,
          docIds: folder.docIds,
        }))
      )
    );
  }

  private persistFolderDeleteMutations(
    workspaceId: string,
    folders: AccessibleFolderMeta[]
  ): void {
    const createdAt = new Date().toISOString();
    const nodes = folders.map(folder =>
      this.folderMetaToMetadataNode(workspaceId, folder)
    );
    this.queuePendingStateWrite(storage =>
      storage.applyLocalMutations(
        nodes,
        folders.map(folder =>
          this.metadataPendingMutation(storage, {
            localMutationId: `folder-delete:${folder.id}`,
            nodeId: folder.id,
            op: 'delete',
            patch: {},
            createdAt,
          })
        )
      )
    );
  }

  /**
   * `removedFolderIds` are folders a complete server window no longer holds
   * (moved out of sight, revoked): they leave the durable projection too, or
   * the materialized-descendant pass below would put them back.
   */
  private async syncToIDB(
    workspaceId: string,
    serverFolders: AccessibleFolderMeta[],
    pageCursor?: { key: string; cursor: string | null },
    removedFolderIds: readonly string[] = []
  ): Promise<AccessibleFolderMeta[]> {
    const storage = this.metadataStore;
    if (!storage) return serverFolders;
    const permissionGraphVersion = this.permissionGraphVersion;

    try {
      await this.pendingStateWrite;
      const serverNodes = serverFolders.map(folder =>
        this.folderMetaToMetadataNode(workspaceId, folder)
      );
      await storage.reconcileServerNodes(serverNodes, removedFolderIds, {
        token: storage.getShapeState().token,
        cursor: storage.getShapeState().cursor,
        pageCursors: pageCursor
          ? {
              ...storage.getShapeState().pageCursors,
              [pageCursor.key]: pageCursor.cursor,
            }
          : storage.getShapeState().pageCursors,
      });
      await storage.applyServerPermissionProjection(serverNodes);
      await storage.updateShapeState({ permissionGraphVersion });
      const visibleNodesById = new Map(
        storage.getVisibleNodes().map(node => [node.nodeId, node])
      );
      const visibleFolders = serverNodes
        .map(node => visibleNodesById.get(node.nodeId) ?? node)
        .filter(node => node.kind === 'folder' && node.folder)
        .map(node => this.metadataNodeToMeta(node));
      const materializedParentIds = new Set(
        Object.keys(storage.getShapeState().pageCursors)
          .filter(key => key.startsWith('children:'))
          .map(key => key.slice('children:'.length))
      );
      const reconciledById = new Map(
        visibleFolders.map(folder => [folder.id, folder])
      );
      const materializedNodes = storage
        .getVisibleNodes()
        .filter(node => node.kind === 'folder' && node.folder);
      let addedNode = true;
      while (addedNode) {
        addedNode = false;
        for (const node of materializedNodes) {
          if (
            reconciledById.has(node.nodeId) ||
            !node.parentId ||
            !reconciledById.has(node.parentId) ||
            (node.syncState !== 'local' &&
              !materializedParentIds.has(node.parentId))
          ) {
            continue;
          }
          const folder = this.metadataNodeToMeta(node);
          if (!isProjectionSystemContainer(folder)) {
            reconciledById.set(folder.id, folder);
            addedNode = true;
          }
        }
      }
      return this.applyOptimisticTrashFilter(
        this.applyOrderState([...reconciledById.values()])
      );
    } catch {
      // IDB write failure — non-fatal, server data is still in memory
      return serverFolders;
    }
  }

  private trashedFolderItemsFromPage(
    page: WorkspaceMetadataPage
  ): AccessibleTrashedFolderMeta[] {
    return page.items
      .filter(item => item.type === 'folder')
      .map(item => ({
        id: item.id,
        workspaceId: this.workspaceId ?? '',
        parentId: item.parentId ?? null,
        name: item.title,
        systemKind: item.folder?.systemKind ?? null,
        trashedAt: item.trashedAt ?? null,
        trashedBy: item.trashedBy ?? null,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        docIds: [],
        hasChildren: item.hasChildren ?? true,
        childCount: item.childCount ?? null,
        subtreeRevision: item.subtreeRevision ?? item.revision ?? null,
        permissionGraphVersion: page.permissionGraphVersion,
        permissions:
          (this.authorityStore?.getFolderPermissions(
            item.id
          ) as FolderPermissions | null) ?? null,
        folderMetadata: item.folder ?? null,
      }));
  }

  private trashedFolderToMetadataNode(
    folder: AccessibleTrashedFolderMeta
  ): WorkspaceListMetadata {
    return {
      workspaceId: folder.workspaceId,
      nodeId: folder.id,
      kind: 'trash-folder',
      title: folder.name,
      parentId: folder.parentId,
      areaId: null,
      sortKey: `${folder.updatedAt}:${folder.id}`,
      hasChildren: folder.hasChildren,
      childCount: folder.childCount,
      subtreeRevision: folder.subtreeRevision,
      trashedAt: folder.trashedAt,
      trashedBy: folder.trashedBy,
      createdAt: folder.createdAt,
      updatedAt: folder.updatedAt,
      permissionHint: 'read',
      syncState: 'fresh',
      docIds: [...folder.docIds],
      folder: folder.folderMetadata ?? undefined,
    };
  }

  private metadataNodeToTrashedFolder(
    node: WorkspaceListMetadata
  ): AccessibleTrashedFolderMeta {
    return {
      id: node.nodeId,
      workspaceId: node.workspaceId,
      parentId: node.parentId,
      name: node.title,
      systemKind: node.folder?.systemKind ?? null,
      trashedAt: node.trashedAt,
      trashedBy: node.trashedBy ?? null,
      createdAt: node.createdAt ?? node.updatedAt,
      updatedAt: node.updatedAt,
      docIds: [...(node.docIds ?? [])],
      hasChildren: node.hasChildren,
      childCount: node.childCount,
      subtreeRevision: node.subtreeRevision,
      permissionGraphVersion: this.permissionGraphVersion,
      permissions:
        (this.authorityStore?.getFolderPermissions(
          node.nodeId
        ) as FolderPermissions | null) ?? null,
      folderMetadata: node.folder ?? null,
    };
  }

  private async hydrateTrashedFoldersLocal(): Promise<void> {
    await this.authorityStore?.hydrateLocal();
    this.observePermissionGraphVersion(
      this.authorityStore?.getPermissionGraphVersion() ?? 0
    );
    const storage = this.trashMetadataStore;
    if (!storage) return;
    if (this.trashHydratedStore === storage) return;
    this.trashHydratedStore = storage;
    await storage.hydrate();
    const cached = storage
      .getVisibleNodes()
      .filter(node => node.kind === 'trash-folder')
      .map(node => this.metadataNodeToTrashedFolder(node));
    if (cached.length > 0) {
      this.trashedFolders$.setValue(cached);
    }
    this.trashedFolderRootIds.clear();
    const cachedIds = new Set(cached.map(folder => folder.id));
    for (const folder of cached) {
      if (!folder.parentId || !cachedIds.has(folder.parentId)) {
        this.trashedFolderRootIds.add(folder.id);
      }
    }
    const pageCursors = storage.getShapeState().pageCursors;
    this.trashedFoldersNextCursor = pageCursors.roots ?? null;
    for (const [key, cursor] of Object.entries(pageCursors)) {
      if (!key.startsWith('children:')) continue;
      const folderId = key.slice('children:'.length);
      this.trashedFolderChildrenNextCursor.set(folderId, cursor);
      this.loadedTrashedFolderChildren.add(folderId);
    }
    this.hasNextTrashedFolderPage$.setValue(
      this.trashedFoldersNextCursor !== null
    );
  }

  async fetchTrashedFolders(
    options: {
      force?: boolean;
      loadMore?: boolean;
      signal?: AbortSignal;
    } = {}
  ): Promise<void> {
    if (this.fetchTrashedFoldersPromise) {
      return this.fetchTrashedFoldersPromise;
    }
    const request = this.fetchTrashedFoldersInternal(options).finally(() => {
      if (this.fetchTrashedFoldersPromise === request) {
        this.fetchTrashedFoldersPromise = null;
      }
    });
    this.fetchTrashedFoldersPromise = request;
    return request;
  }

  private async fetchTrashedFoldersInternal(options: {
    force?: boolean;
    loadMore?: boolean;
    signal?: AbortSignal;
  }): Promise<void> {
    await this.hydrateTrashedFoldersLocal();
    const workspaceId = this.workspaceId;
    if (!workspaceId || isBackendKnownUnavailable()) return;
    const after = options.loadMore ? this.trashedFoldersNextCursor : null;
    if (options.loadMore && !after) return;
    const query = new URLSearchParams({ first: '100' });
    if (after) query.set('after', after);
    const page = await defaultWorkspaceApiClient.requestJson<
      WorkspaceMetadataPage & {
        permissionSnapshots?: PermissionSnapshotResponse[];
      }
    >({
      workspaceId,
      category: 'metadata',
      key: `trash-folders:${after ?? 'first'}`,
      mode: 'background',
      priority: 'visible',
      path: `/api/workspaces/${workspaceId}/metadata/trash/folders?${query}`,
      init: {
        signal: options.signal ?? this.lifecycleAbortController.signal,
      },
    });
    if (page.permissionSnapshots) {
      await this.authorityStore?.applyOnlineSnapshots({
        snapshots: page.permissionSnapshots,
      });
    }
    this.observePermissionGraphVersion(page.permissionGraphVersion);
    const incoming = this.trashedFolderItemsFromPage(page);
    const current = this.trashedFolders$.value;
    const previousRootIds = new Set(this.trashedFolderRootIds);
    const incomingRootIds = new Set(incoming.map(folder => folder.id));
    const currentById = new Map(current.map(folder => [folder.id, folder]));
    const retainedDescendants = options.loadMore
      ? current
      : current.filter(folder => {
          if (previousRootIds.has(folder.id)) return false;
          let parentId = folder.parentId;
          const visited = new Set<string>();
          while (parentId && !visited.has(parentId)) {
            if (incomingRootIds.has(parentId)) return true;
            visited.add(parentId);
            parentId = currentById.get(parentId)?.parentId ?? null;
          }
          return false;
        });
    const existing = options.loadMore
      ? current
      : [...incoming, ...retainedDescendants];
    const byId = new Map(existing.map(folder => [folder.id, folder]));
    for (const folder of incoming) {
      const materialized = currentById.get(folder.id);
      byId.set(folder.id, {
        ...folder,
        docIds: materialized?.docIds ?? folder.docIds,
        hasChildren: folder.hasChildren || materialized?.hasChildren === true,
      });
    }
    const next = [...byId.values()];
    this.trashedFolders$.setValue(next);
    if (!options.loadMore) this.trashedFolderRootIds.clear();
    for (const folder of incoming) this.trashedFolderRootIds.add(folder.id);
    this.trashedFoldersNextCursor = page.pageInfo.hasNextPage
      ? page.pageInfo.endCursor
      : null;
    this.hasNextTrashedFolderPage$.setValue(
      this.trashedFoldersNextCursor !== null
    );
    const storage = this.trashMetadataStore;
    if (storage) {
      const removedNodeIds = options.loadMore
        ? []
        : current
            .filter(folder => !byId.has(folder.id))
            .map(folder => folder.id);
      await storage.reconcileServerNodes(
        next.map(folder => this.trashedFolderToMetadataNode(folder)),
        removedNodeIds,
        {
          token: storage.getShapeState().token,
          cursor: this.trashedFoldersNextCursor,
          pageCursors: {
            ...storage.getShapeState().pageCursors,
            roots: this.trashedFoldersNextCursor,
          },
        }
      );
    }
  }

  hasMoreTrashedFolderChildren(folderId: string): boolean {
    return this.trashedFolderChildrenNextCursor.get(folderId) != null;
  }

  async fetchTrashedFolderChildren(
    folderId: string,
    options: { force?: boolean; loadMore?: boolean; signal?: AbortSignal } = {}
  ): Promise<void> {
    await this.hydrateTrashedFoldersLocal();
    const workspaceId = this.workspaceId;
    if (!workspaceId || isBackendKnownUnavailable()) return;
    const after = options.loadMore
      ? this.trashedFolderChildrenNextCursor.get(folderId)
      : null;
    if (options.loadMore && !after) return;
    if (
      !options.force &&
      !options.loadMore &&
      this.loadedTrashedFolderChildren.has(folderId)
    ) {
      return;
    }
    const query = new URLSearchParams({ first: '100', view: 'trash' });
    if (after) query.set('after', after);
    const page = await this.fetchFolderChildrenPage(
      workspaceId,
      folderId,
      query,
      options.signal ?? this.lifecycleAbortController.signal
    );
    const children = this.trashedFolderItemsFromPage(page);
    const docIds = page.items
      .filter(item => item.type === 'doc')
      .map(item => item.id);
    const byId = new Map(
      this.trashedFolders$.value.map(folder => [folder.id, folder])
    );
    const parent = byId.get(folderId);
    if (parent) {
      byId.set(folderId, {
        ...parent,
        docIds: options.loadMore
          ? [...new Set([...parent.docIds, ...docIds])]
          : docIds,
      });
    }
    for (const child of children) byId.set(child.id, child);
    const next = [...byId.values()];
    this.trashedFolders$.setValue(next);
    this.trashedFolderChildrenNextCursor.set(
      folderId,
      page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null
    );
    this.loadedTrashedFolderChildren.add(folderId);
    const storage = this.trashMetadataStore;
    if (storage) {
      const persistedFolders = [
        ...(parent ? [byId.get(folderId)!] : []),
        ...children,
      ];
      await storage.applyServerNodes(
        persistedFolders.map(folder =>
          this.trashedFolderToMetadataNode(folder)
        ),
        {
          token: storage.getShapeState().token,
          cursor: storage.getShapeState().cursor,
          pageCursors: {
            ...storage.getShapeState().pageCursors,
            [`children:${folderId}`]:
              this.trashedFolderChildrenNextCursor.get(folderId) ?? null,
          },
        }
      );
    }
  }

  removeTrashedFolderSubtreeLocally(folderIds: readonly string[]): void {
    const removed = new Set(folderIds);
    this.trashedFolders$.setValue(
      this.trashedFolders$.value.filter(folder => !removed.has(folder.id))
    );
    void this.trashMetadataStore?.removeNodes(folderIds);
  }

  // ─── Event Listeners ────────────────────────────────────────────────

  private startReconnectListener(): void {
    if (typeof window === 'undefined') return;

    this.onlineHandler = () => this.scheduleConnectivityRefresh();

    window.addEventListener('online', this.onlineHandler);
  }

  private startBackendAvailabilityListener(): void {
    if (this.backendAvailabilityUnsubscribe) return;

    this.backendAvailabilityUnsubscribe = onBackendAvailable(() => {
      this.scheduleConnectivityRefresh();
    });
  }

  private scheduleConnectivityRefresh(): void {
    if (this.disposed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.disposed) return;
      this.fetchAccessibleFolders()
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(0);
          }
        })
        .catch(() => {});
    }, 1000);
  }

  private startVisibilityListener(): void {
    if (typeof document === 'undefined') return;

    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.serverFetchCompleted) return;
      this.fetchAccessibleFolders().catch(() => {});
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  reconcilePermissionChange(
    event: WorkspacePermissionChange
  ): Promise<FolderPermissionReconciliationResult> | undefined {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;
    this.folderChildrenReadGeneration++;

    recordWorkspaceProjectionDiagnostic({
      stage: 'folder-cache:permission-received',
      changeType: event.changeType,
      docIds: event.affectedDocIds,
      folderIds: event.affectedFolderIds,
    });

    // The permission store applies the event itself; passing its counter
    // on as a list value would mark every stored grant stale.
    this.observePermissionGraphVersion(event.permissionGraphVersion);
    if (event.refreshMode === 'lazy') return;

    const affectedFolderIds = event.affectedFolderIds ?? [];
    const scoped =
      event.refreshMode === 'scoped' ||
      (event.refreshMode !== 'full' &&
        (affectedFolderIds.length > 0 ||
          (event.affectedDocIds?.length ?? 0) > 0));
    if (scoped) {
      if (event.changeType === 'folder_created') {
        // The recipient does not have the new folder in its projection yet,
        // so an ID-scoped shape refresh cannot resolve its parent or contents.
        // The metadata delta adds the new row without resetting the sidebar.
        void this.fetchAccessibleFolders().catch(() => {});
        return;
      }
      if (
        affectedFolderIds.length > 0 &&
        permissionEventMayChangeFolderDetails(event.changeType)
      ) {
        this.folderDetailsInvalidated$.next(new Set(affectedFolderIds));
      }
      if (
        affectedFolderIds.length > 0 &&
        (event.changeType === 'folder_role_granted' ||
          event.changeType === 'folder_role_revoked' ||
          event.changeType === 'folder_inheritance_changed')
      ) {
        // A Visible folder can already exist in the recipient's projection as
        // a metadata-only row. Refreshing only its loaded parent/child shape
        // then does nothing: that row has no expandable child window yet.
        //
        // The folder's own bounded child endpoint is authoritative for both
        // transitions. It returns the parent with the recipient's current
        // metadataOnly state and at most the first 100 readable direct
        // children, so grant and revoke update the same row without a global
        // projection reset or workspace-sized fan-out.
        return this.reconcilePermissionChangedFolderWindows(
          affectedFolderIds,
          event.changeType
        ).catch(() => ({ grantedDocIds: [], revokedDocIds: [] }));
      }
      if (
        (event.changeType === 'folder_role_granted' ||
          event.changeType === 'folder_visibility_changed') &&
        affectedFolderIds.some(folderId => !this.getCachedFolder(folderId))
      ) {
        // A newly visible root has no local parent shape yet. First apply its
        // bounded metadata delta, then fetch only the first direct-child
        // window. This restores at most one materialized page of Document
        // metadata without rebuilding or walking the whole workspace tree.
        return this.hydrateVisibleFolderRoots(affectedFolderIds).catch(() => ({
          grantedDocIds: [],
          revokedDocIds: [],
        }));
      }
      if (
        event.changeType === 'doc_created' &&
        affectedFolderIds.length === 1 &&
        event.affectedDocIds?.length === 1
      ) {
        const docId = event.affectedDocIds[0];
        const eventFolderId = affectedFolderIds[0];
        const effectiveFolderId =
          this.loadPendingDocAssignments().find(
            assignment => assignment.docId === docId
          )?.folderId ?? eventFolderId;
        if (
          this.applyScopedDocPlacement(
            eventFolderId,
            docId,
            'doc-created-placement-applied'
          )
        ) {
          if (this.getCachedFolder(effectiveFolderId)?.isWorkspaceRoot) {
            return;
          }
          // A direct placement makes the document discoverable immediately,
          // but a previously empty remote folder has no materialized child
          // window yet. Reconcile that one bounded window so the sidebar gains
          // the child row and expansion affordance without a workspace reset.
          return this.fetchFolderChildren(effectiveFolderId, {
            force: true,
            diagnosticReason: 'permission-event:doc_created',
          })
            .then(() => ({
              grantedDocIds: [docId],
              revokedDocIds: [],
            }))
            .catch(() => ({
              grantedDocIds: [],
              revokedDocIds: [],
            }));
        }
      }
      if (
        event.changeType === 'doc_moved' &&
        event.docPlacementChanges?.length &&
        event.docPlacementChanges.every(change =>
          Boolean(this.getCachedFolder(change.toFolderId))
        ) &&
        event.docPlacementChanges.every(change =>
          this.applyScopedDocPlacement(
            change.toFolderId,
            change.docId,
            'doc-moved-placement-applied'
          )
        )
      ) {
        return;
      }
      if (
        event.changeType === 'folder_moved' &&
        event.folderPlacementChanges?.length
      ) {
        if (this.applyScopedFolderPlacements(event.folderPlacementChanges)) {
          return;
        }
        // Moving a Folder changes only the old/new parent windows, never the
        // contents of the moved Folder itself. If the direct placement cannot
        // be applied (for example because a local optimistic parent differs
        // from the recipient-filtered source), reconcile only the parents.
        // Refetching the moved child window repeats an identical read and does
        // not help discover its new placement.
        const movedFolderIds = new Set(
          event.folderPlacementChanges.map(change => change.folderId)
        );
        const parentFolderIds = affectedFolderIds.filter(
          folderId => !movedFolderIds.has(folderId)
        );
        if (parentFolderIds.length > 0) {
          this.queueScopedFolderShapeRefresh(
            parentFolderIds,
            'permission-event:folder_moved-parent-fallback'
          );
        }
        return;
      }
      if (affectedFolderIds.length > 0) {
        this.queueScopedFolderShapeRefresh(
          affectedFolderIds,
          `permission-event:${event.changeType}`
        );
      }
      return;
    }

    if (permissionEventMayChangeFolderDetails(event.changeType)) {
      this.folderDetailsInvalidated$.next(null);
    }
    this.queuePermissionProjectionRefresh(event.permissionGraphVersion);
    return;
  }

  private async hydrateVisibleFolderRoots(
    folderIds: readonly string[]
  ): Promise<FolderPermissionReconciliationResult> {
    await this.fetchAccessibleFolders();
    const restoredDocIds = new Set<string>();
    // Serial reads provide backpressure if one permission event ever names
    // several independent roots. Each read remains cursor-bounded to the
    // first 100 direct children; deeper pages hydrate on normal UI demand.
    for (const folderId of new Set(folderIds)) {
      if (!this.getCachedFolder(folderId)) continue;
      await this.fetchFolderChildren(folderId, {
        force: true,
        diagnosticReason: 'permission-event:folder-visible',
        reuseRecent: true,
      });
      for (const docId of this.getCachedFolder(folderId)?.docIds ?? []) {
        restoredDocIds.add(docId);
      }
    }
    return {
      grantedDocIds: [...restoredDocIds],
      revokedDocIds: [],
    };
  }

  private async reconcilePermissionChangedFolderWindows(
    folderIds: readonly string[],
    changeType:
      | 'folder_role_granted'
      | 'folder_role_revoked'
      | 'folder_inheritance_changed'
  ): Promise<FolderPermissionReconciliationResult> {
    const materializedDocsBefore = new Map<string, Set<string>>();
    const foldersBefore = this.cache$.value;
    for (const folderId of new Set(folderIds)) {
      const subtreeFolderIds = new Set([folderId]);
      let added = true;
      while (added) {
        added = false;
        for (const folder of foldersBefore) {
          if (
            folder.parentId &&
            subtreeFolderIds.has(folder.parentId) &&
            !subtreeFolderIds.has(folder.id)
          ) {
            subtreeFolderIds.add(folder.id);
            added = true;
          }
        }
      }
      materializedDocsBefore.set(
        folderId,
        new Set(
          foldersBefore
            .filter(folder => subtreeFolderIds.has(folder.id))
            .flatMap(folder => folder.docIds)
        )
      );
    }

    const grantedDocIds = new Set<string>();
    const revokedDocIds = new Set<string>();
    // Serial, cursor-bounded reads provide backpressure if a group event ever
    // names several roots. No traversal is performed; deeper windows remain
    // demand-loaded through the normal folder expansion path.
    for (const folderId of new Set(folderIds)) {
      await this.fetchFolderChildren(folderId, {
        force: true,
        diagnosticReason: `permission-event:${changeType}`,
        reuseRecent: true,
      });
      const reconciled = this.getCachedFolder(folderId);
      if (reconciled?.metadataOnly) {
        for (const docId of materializedDocsBefore.get(folderId) ?? []) {
          revokedDocIds.add(docId);
        }
      } else {
        for (const docId of reconciled?.docIds ?? []) {
          grantedDocIds.add(docId);
        }
      }
    }
    // The event may already carry direct Document IDs. The central reconciler
    // deduplicates cache reads/removals, while this result also covers
    // inheritance changes whose recipient-safe event intentionally names only
    // the visible Folder root.
    return {
      grantedDocIds: [...grantedDocIds],
      revokedDocIds: [...revokedDocIds],
    };
  }

  private applyScopedDocPlacement(
    folderId: string,
    docId: string,
    diagnosticStage:
      | 'doc-created-placement-applied'
      | 'doc-moved-placement-applied'
  ): boolean {
    const workspaceId = this.workspaceId;
    const pendingTarget =
      diagnosticStage === 'doc-created-placement-applied'
        ? this.loadPendingDocAssignments().find(
            assignment => assignment.docId === docId
          )?.folderId
        : undefined;
    // The first persisted update of a locally-created document can predate its
    // placement metadata. In that window the server emits doc_created for the
    // workspace root. Keep the local creation assignment authoritative until
    // a server window materializes the document in that target folder.
    const effectiveFolderId = pendingTarget ?? folderId;
    const target = this.getCachedFolder(effectiveFolderId);
    if (!workspaceId || !target) return false;

    let foldersChanged = false;
    const folders = this.cache$.value.map(folder => {
      const withoutDoc = folder.docIds.filter(id => id !== docId);
      const nextDocIds =
        folder.id === effectiveFolderId ? [docId, ...withoutDoc] : withoutDoc;
      if (
        nextDocIds.length === folder.docIds.length &&
        nextDocIds.every((id, index) => id === folder.docIds[index])
      ) {
        return folder;
      }
      foldersChanged = true;
      return { ...folder, docIds: nextDocIds };
    });

    const currentRootDocIds = this.rootFolderDocIds$.value;
    const rootWithoutDoc = currentRootDocIds.filter(id => id !== docId);
    const nextRootDocIds = target.isWorkspaceRoot
      ? [docId, ...rootWithoutDoc]
      : rootWithoutDoc;
    const rootChanged =
      nextRootDocIds.length !== currentRootDocIds.length ||
      nextRootDocIds.some((id, index) => id !== currentRootDocIds[index]);

    if (foldersChanged) {
      this.cache$.setValue(folders);
      this.saveOrderState(folders);
      this.saveLocalFolderStateToIDB(folders);
    }
    if (rootChanged) {
      this.setRootFolderDocIds(workspaceId, nextRootDocIds);
    }
    recordWorkspaceProjectionDiagnostic({
      stage: `folder-cache:${diagnosticStage}`,
      docIds: [docId],
      folderIds: [effectiveFolderId],
    });
    return true;
  }

  private applyScopedFolderPlacements(
    changes: readonly {
      folderId: string;
      fromParentId: string | null;
      toParentId: string;
    }[]
  ): boolean {
    const workspaceId = this.workspaceId;
    if (!workspaceId || changes.length === 0) return false;

    const foldersById = new Map(
      this.cache$.value.map(folder => [folder.id, folder] as const)
    );
    if (
      changes.some(change => {
        const folder = foldersById.get(change.folderId);
        return (
          !folder ||
          !foldersById.has(change.toParentId) ||
          (folder.parentId !== change.fromParentId &&
            folder.parentId !== change.toParentId)
        );
      })
    ) {
      return false;
    }

    const targetParentByFolderId = new Map(
      changes.map(change => [change.folderId, change.toParentId] as const)
    );
    let changed = false;
    const folders = this.cache$.value.map(folder => {
      const toParentId = targetParentByFolderId.get(folder.id);
      if (!toParentId || folder.parentId === toParentId) return folder;
      changed = true;
      return { ...folder, parentId: toParentId };
    });

    if (changed) {
      this.cache$.setValue(folders);
      this.saveOrderState(folders);
      this.saveLocalFolderStateToIDB(folders);
    }
    recordWorkspaceProjectionDiagnostic({
      stage: 'folder-cache:folder-moved-placement-applied',
      folderIds: changes.flatMap(change => [
        change.folderId,
        change.toParentId,
      ]),
    });
    return true;
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;
    if (FOLDER_SHAPE_METADATA_CHANGES.has(event.changeType)) {
      this.folderChildrenReadGeneration++;
    }

    if (event.changeType === 'folder_trashed') {
      if (event.docId) {
        this.materializeTrashedSubtreeFromCurrentProjection(
          event.docId,
          event.timestamp
        );
        this.removeAuthoritativeFolderSubtree(event.docId);
      } else {
        // Removal events are intentionally anonymous after access disappears.
        // Reconcile the active projection immediately instead of waiting for
        // the later permission refresh.
        void this.fetchAccessibleFolders().catch(() => {});
      }
    }

    // Create/move/restore have a recipient-filtered permission event that owns
    // their targeted/full folder reconciliation. Trash is reconciled above
    // because removal events become anonymous once active access disappears.
    // Scalar rename and permanent deletion have no equivalent shape event.
    if (
      event.changeType === 'folder_metadata_updated' ||
      event.changeType === 'folder_restored' ||
      event.changeType === 'folder_deleted' ||
      event.changeType === 'doc_visibility_changed'
    ) {
      void this.fetchAccessibleFolders().catch(() => {});
    }
    if (
      event.changeType === 'folder_trashed' ||
      event.changeType === 'folder_restored' ||
      event.changeType === 'folder_deleted'
    ) {
      void this.fetchTrashedFolders({ force: true }).catch(() => {});
    }
  }

  private materializeTrashedSubtreeFromCurrentProjection(
    rootFolderId: string,
    timestamp: number
  ): void {
    const root = this.cache$.value.find(folder => folder.id === rootFolderId);
    const workspaceId = this.workspaceId;
    if (!root || !workspaceId) return;

    const subtreeIds = new Set([rootFolderId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const folder of this.cache$.value) {
        if (
          folder.parentId &&
          subtreeIds.has(folder.parentId) &&
          !subtreeIds.has(folder.id)
        ) {
          subtreeIds.add(folder.id);
          changed = true;
        }
      }
    }

    const trashedAt = new Date(timestamp).toISOString();
    const projected = this.cache$.value
      .filter(folder => subtreeIds.has(folder.id))
      .map<AccessibleTrashedFolderMeta>(folder => ({
        id: folder.id,
        workspaceId,
        parentId: folder.parentId,
        name: folder.name,
        systemKind: folder.systemKind,
        trashedAt,
        trashedBy: null,
        createdAt: trashedAt,
        updatedAt: trashedAt,
        docIds: [...folder.docIds],
        hasChildren:
          folder.docIds.length > 0 ||
          this.cache$.value.some(child => child.parentId === folder.id),
        childCount: null,
        subtreeRevision: folder.revision ?? null,
        permissionGraphVersion:
          folder.permissionGraphVersion ?? this.permissionGraphVersion,
        permissions: this.getLocalFolderPermissions(folder.id),
        folderMetadata: {
          ownerUserId: folder.ownerUserId,
          systemKind: folder.systemKind ?? null,
          visibility: folder.visibility,
          inheritPermissions: folder.inheritPermissions,
          isWorkspaceRoot: folder.isWorkspaceRoot,
          immutable: folder.immutable,
          storageLimitBytes: folder.storageLimitBytes,
          storageUsedBytes: folder.storageUsedBytes,
          metadataOnly: folder.metadataOnly,
        },
      }));
    const byId = new Map(
      this.trashedFolders$.value.map(folder => [folder.id, folder])
    );
    for (const folder of projected) byId.set(folder.id, folder);
    this.trashedFolders$.setValue([...byId.values()]);
  }

  private queueScopedFolderShapeRefresh(
    folderIds: string[],
    diagnosticReason = 'permission-projection-refresh'
  ): void {
    for (const folderId of folderIds) {
      this.scopedFolderRefreshIds.add(folderId);
    }
    if (this.scopedFolderRefreshScheduled) return;
    this.scopedFolderRefreshScheduled = true;

    queueMicrotask(() => {
      this.scopedFolderRefreshScheduled = false;
      const affectedFolderIds = [...this.scopedFolderRefreshIds];
      this.scopedFolderRefreshIds.clear();
      this.permissionRefreshPromise = this.permissionRefreshPromise
        .then(async () => {
          if (this.disposed) return;
          const parentIds = new Set<string>();
          for (const folderId of affectedFolderIds) {
            const folder = this.getCachedFolder(folderId);
            const hasMaterializedChildren = this.cache$.value.some(
              candidate => candidate.parentId === folderId
            );
            if (
              folder?.parentId &&
              this.loadedFolderChildren.has(folder.parentId)
            ) {
              parentIds.add(folder.parentId);
            }
            if (
              folder?.isWorkspaceRoot ||
              this.loadedFolderChildren.has(folderId) ||
              hasMaterializedChildren
            ) {
              parentIds.add(folderId);
            }
          }
          await this.forgetStoredChildWindows(parentIds);
          await Promise.all(
            [...parentIds].map(parentId =>
              this.fetchFolderChildren(parentId, {
                force: true,
                diagnosticReason,
                reuseRecent: true,
              })
            )
          );
        })
        .catch(() => undefined);
    });
  }

  private queuePermissionProjectionRefresh(
    permissionGraphVersion?: number
  ): void {
    this.permissionRefreshPromise = this.permissionRefreshPromise
      .then(async () => {
        if (this.disposed) return;
        await this.fetchAccessibleFoldersPromise?.catch(() => undefined);
        if (this.disposed) return;
        if (
          permissionGraphVersion !== undefined &&
          this.completedAuthoritativeProjectionVersion >= permissionGraphVersion
        ) {
          return;
        }
        const materializedFolderIds = [...this.loadedFolderChildren];
        await this.resetAuthoritativeProjection(permissionGraphVersion);
        // A root snapshot carries only root-level document placements. Keep
        // the previously materialized child windows marked as such while the
        // replacement snapshot is published, so their last authoritative
        // docIds remain mounted until the forced child reads below replace
        // them. Clearing this marker first briefly published every expanded
        // Dynamic Area/Folder with an empty document list.
        for (const folderId of materializedFolderIds) {
          this.loadedFolderChildren.add(folderId);
        }
        await this.fetchAccessibleFolders();
        const childWindowIds = materializedFolderIds.filter(folderId => {
          const folder = this.getCachedFolder(folderId);
          return folder !== null && !folder.isWorkspaceRoot;
        });
        await this.forgetStoredChildWindows(childWindowIds);
        await Promise.all(
          childWindowIds.map(folderId =>
            this.fetchFolderChildren(folderId, {
              force: true,
              reuseRecent: true,
            })
          )
        );
        this.completedAuthoritativeProjectionVersion = Math.max(
          this.completedAuthoritativeProjectionVersion,
          permissionGraphVersion ?? 0
        );
      })
      .catch(() => undefined);
  }

  private async resetAuthoritativeProjection(
    permissionGraphVersion?: number
  ): Promise<void> {
    this.folderChildrenReadGeneration++;
    await this.reconcileMetadata(async () => {
      await this.pendingStateWrite;
      const storage = this.metadataStore;
      await storage?.resetServerProjection();
      this.observePermissionGraphVersion(permissionGraphVersion);
      this.authorityStore?.observePermissionGraphVersion(
        permissionGraphVersion ?? 0
      );
      this.metadataDeltaToken = null;
      this.folderChildrenNextCursor.clear();
      this.loadedFolderChildren.clear();
      // Keep the last painted projection mounted while its replacement is
      // fetched. Publishing the just-reset storage state here briefly emptied
      // every sidebar section on access revocation. The following
      // authoritative root snapshot replaces this cache atomically.
    });
  }

  // ─── Cleanup ────────────────────────────────────────────────────────

  override dispose(): void {
    this.disposed = true;
    this.lifecycleAbortController.abort(
      new DOMException('Workspace folder cache disposed', 'AbortError')
    );
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.backendAvailabilityUnsubscribe) {
      this.backendAvailabilityUnsubscribe();
      this.backendAvailabilityUnsubscribe = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.folderDetailsInvalidated$.complete();
    super.dispose();
  }

  // ─── Public Observables ─────────────────────────────────────────────

  /**
   * Observable of accessible folder IDs (includes metadataOnly folders).
   */
  watchAccessibleFolderIds(): Observable<string[]> {
    this.ensureFetched();
    return this.cache$.pipe(
      map((folders: AccessibleFolderMeta[]) =>
        folders.map((f: AccessibleFolderMeta) => f.id)
      )
    );
  }

  /**
   * Observable of all accessible folder metadata.
   */
  watchAllAccessibleFolders(): Observable<AccessibleFolderMeta[]> {
    this.ensureFetched();
    return this.cache$.pipe(map((folders: AccessibleFolderMeta[]) => folders));
  }

  /**
   * Synchronous lookup of a cached folder by ID.
   */
  getCachedFolder(folderId: string): AccessibleFolderMeta | null {
    return (
      this.cache$.value.find((f: AccessibleFolderMeta) => f.id === folderId) ??
      null
    );
  }

  getCachedWorkspaceRootFolderId(): string | null {
    return (
      this.cache$.value.find(
        (folder: AccessibleFolderMeta) => folder.isWorkspaceRoot
      )?.id ?? null
    );
  }

  getCachedRootFolderDocIds(): string[] {
    return [...this.rootFolderDocIds$.value];
  }

  /**
   * Synchronous set of all cached accessible folder IDs.
   * Used by FolderDocSyncService to skip trashed folders during reconcile.
   */
  getCachedFolderIds(): Set<string> {
    return new Set(this.cache$.value.map((f: AccessibleFolderMeta) => f.id));
  }

  /**
   * Server-derived doc links by folder. Boot reconciliation must only replay
   * links that the server-derived cache still confirms; stale local Y.js links
   * from permanently deleted docs must not be written back.
   */
  getCachedFolderDocLinkKeys(): Set<string> {
    const links = new Set<string>();
    for (const folder of this.cache$.value) {
      for (const docId of folder.docIds) {
        links.add(`${folder.id}\u0000${docId}`);
      }
    }
    return links;
  }

  /**
   * sl-sffq.2: Synchronous list of all cached accessible folders.
   * Used by FolderDocSyncService.reconcileTrashedStatus() to compare with Y.js.
   * Server-filtered: only contains non-trashed, accessible folders.
   */
  getCachedFolders(): AccessibleFolderMeta[] {
    return this.cache$.value;
  }

  watchOptimisticallyTrashedFolders$(): Observable<
    AccessibleFolderOptimisticTrashRecord[]
  > {
    this.loadOptimisticTrashState();
    return this.optimisticTrashedFolders$.pipe(
      map(records => records.map(record => this.cloneTrashRecord(record)))
    );
  }

  watchTrashedFolderMetadata$(): Observable<AccessibleTrashedFolderMeta[]> {
    void this.hydrateTrashedFoldersLocal()
      .then(() => this.fetchTrashedFolders())
      .catch(() => {
        // Cached trash metadata remains the local-first render authority.
      });
    this.loadOptimisticTrashState();
    return combineLatest([
      this.trashedFolders$,
      this.optimisticTrashedFolders$,
    ]).pipe(
      map(([serverFolders, optimisticRecords]) => {
        const byId = new Map(serverFolders.map(folder => [folder.id, folder]));
        for (const record of optimisticRecords) {
          for (const folder of record.folders) {
            byId.set(folder.id, {
              id: folder.id,
              workspaceId: record.workspaceId,
              parentId: folder.parentId,
              name: folder.name,
              systemKind: folder.systemKind,
              trashedAt: record.trashedAt,
              trashedBy: null,
              createdAt: record.trashedAt,
              updatedAt: record.trashedAt,
              docIds: [...folder.docIds],
              hasChildren: record.folders.some(
                child => child.parentId === folder.id
              ),
              childCount: null,
              subtreeRevision: null,
              permissionGraphVersion: this.permissionGraphVersion,
              permissions: null,
              folderMetadata: {
                ownerUserId: folder.ownerUserId,
                systemKind: folder.systemKind,
                visibility: folder.visibility,
                inheritPermissions: folder.inheritPermissions,
                isWorkspaceRoot: folder.isWorkspaceRoot,
                immutable: folder.immutable,
                storageLimitBytes: folder.storageLimitBytes,
                storageUsedBytes: folder.storageUsedBytes,
                metadataOnly: folder.metadataOnly,
              },
            });
          }
        }
        return [...byId.values()].sort(
          (left, right) =>
            left.name.localeCompare(right.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }) || left.id.localeCompare(right.id)
        );
      })
    );
  }

  getOptimisticallyTrashedFolders(): AccessibleFolderOptimisticTrashRecord[] {
    return this.loadOptimisticTrashState().map(record =>
      this.cloneTrashRecord(record)
    );
  }

  private findOptimisticTrashRecordForFolder(
    folderId: string
  ): AccessibleFolderOptimisticTrashRecord | null {
    return (
      this.loadOptimisticTrashState().find(
        record =>
          record.id === folderId ||
          record.folders.some(folder => folder.id === folderId)
      ) ?? null
    );
  }

  hasCompletedServerFetch(): boolean {
    return this.serverFetchCompleted;
  }

  getCachedFolderTreeDocIds(folderId: string): string[] {
    const result = new Set<string>();
    const folders = this.cache$.value;
    const stack = [folderId];

    while (stack.length > 0) {
      const currentId = stack.pop()!;
      const folder = folders.find(item => item.id === currentId);
      if (!folder) {
        continue;
      }
      for (const docId of folder.docIds) {
        result.add(docId);
      }
      for (const child of folders) {
        if (child.parentId === currentId) {
          stack.push(child.id);
        }
      }
    }

    return [...result];
  }

  /**
   * Check if a folder is metadataOnly (user can see name but not contents).
   */
  isMetadataOnly(folderId: string): boolean {
    const folder = this.getCachedFolder(folderId);
    return folder?.metadataOnly ?? false;
  }

  /**
   * Observable of the workspace root folder ID (isWorkspaceRoot === true).
   * Returns null if not found (e.g. local workspace or cache not yet loaded).
   */
  watchWorkspaceRootFolderId$(): Observable<string | null> {
    this.ensureFetched();
    return this.cache$.pipe(
      map(
        (folders: AccessibleFolderMeta[]) =>
          folders.find((f: AccessibleFolderMeta) => f.isWorkspaceRoot)?.id ??
          null
      )
    );
  }

  /**
   * Reactive observable for a folder's metadataOnly status.
   */
  watchIsMetadataOnly$(folderId: string): Observable<boolean> {
    this.ensureFetched();
    return this.cache$.pipe(
      map(
        (folders: AccessibleFolderMeta[]) =>
          folders.find((f: AccessibleFolderMeta) => f.id === folderId)
            ?.metadataOnly ?? false
      )
    );
  }

  /**
   * Observable of doc IDs linked to the workspace root folder.
   * These are explicit PostgreSQL folder links, not Y.js page metadata.
   */
  watchRootFolderDocIds$(): Observable<string[]> {
    this.ensureFetched();
    return this.rootFolderDocIds$;
  }

  /**
   * Refresh only rootFolderDocIds (lighter than fetchAccessibleFolders).
   * Call after doc-folder operations (create doc, move doc, etc.)
   * to keep the organize sidebar in sync with the backend.
   */
  async refreshRootFolderDocIds(): Promise<void> {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;
    const root = this.cache$.value.find(folder => folder.isWorkspaceRoot);
    if (!root) {
      await this.fetchAccessibleFolders();
      return;
    }
    await this.fetchFolderChildren(root.id, {
      force: true,
      diagnosticReason: 'root-folder-doc-ids-refresh',
      reuseRecent: true,
    });
  }

  /**
   * Optimistically remove a doc from the local rootFolderDocIds cache.
   * Called immediately when a doc is moved into a folder (before backend roundtrip)
   * to prevent the doc from appearing in both root and folder simultaneously.
   */
  optimisticRemoveFromRoot(docId: string): void {
    const current = this.rootFolderDocIds$.value;
    if (current.includes(docId)) {
      const updated = current.filter(id => id !== docId);
      this.rootFolderDocIds$.setValue(updated);

      // Sync optimistic removal to IDB
      const wsId = this.workspaceId;
      if (wsId) {
        this.saveRootFolderDocIdsToIDB(updated);
      }
    }
  }

  /**
   * Optimistically remove a moved doc from accessible folder metadata.
   * Used when a private-folder doc is moved into public navigation: the public
   * Y.js link appears immediately, while this cache owns the private sidebar.
   */
  optimisticRemoveDocFromAccessibleFolders(docId: string): void {
    let changed = false;
    const updated = this.cache$.value.map(folder => {
      if (!folder.docIds.includes(docId)) {
        return folder;
      }
      changed = true;
      return {
        ...folder,
        docIds: folder.docIds.filter(id => id !== docId),
      };
    });

    if (!changed) {
      return;
    }

    this.cache$.setValue(updated);
    this.saveOrderState(updated);

    const wsId = this.workspaceId;
    if (wsId) {
      this.saveLocalFolderStateToIDB(updated);
    }
  }

  optimisticRemoveDocEverywhere(docId: string): void {
    this.clearPendingDocFolderAssignment(docId);

    let foldersChanged = false;
    const updatedFolders = this.cache$.value.map(folder => {
      if (!folder.docIds.includes(docId)) {
        return folder;
      }
      foldersChanged = true;
      return {
        ...folder,
        docIds: folder.docIds.filter(id => id !== docId),
      };
    });

    const currentRootDocIds = this.rootFolderDocIds$.value;
    const updatedRootDocIds = currentRootDocIds.filter(id => id !== docId);
    const rootChanged = updatedRootDocIds.length !== currentRootDocIds.length;

    if (!foldersChanged && !rootChanged) {
      return;
    }

    if (foldersChanged) {
      this.cache$.setValue(updatedFolders);
      this.saveOrderState(updatedFolders);
    }
    if (rootChanged) {
      this.rootFolderDocIds$.setValue(updatedRootDocIds);
    }

    const wsId = this.workspaceId;
    if (!wsId) {
      return;
    }
    if (foldersChanged) {
      this.saveLocalFolderStateToIDB(updatedFolders);
    }
    if (rootChanged) {
      this.saveRootFolderDocIdsToIDB(updatedRootDocIds);
    }
  }

  optimisticUpsertFolder(
    folder: AccessibleFolderMeta,
    options: OptimisticFolderUpsertOptions = {}
  ): void {
    if (options.preserveUntilServerAck) {
      this.rememberOptimisticCreatedFolder(folder);
    }

    const existingIndex = this.cache$.value.findIndex(f => f.id === folder.id);
    const next =
      existingIndex === -1
        ? [folder, ...this.cache$.value]
        : this.cache$.value.map(existing =>
            existing.id === folder.id
              ? {
                  ...existing,
                  ...folder,
                  docIds: [...folder.docIds],
                }
              : existing
          );

    this.cache$.setValue(next);
    this.saveOrderState(next);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);

    const wsId = this.workspaceId;
    if (wsId) {
      const node = this.folderMetaToMetadataNode(wsId, folder);
      if (existingIndex === -1) {
        this.authorityStore?.applyPendingLocalResourceOwner(
          'folder',
          folder.id,
          `folder-create:${folder.id}`
        );
        this.queuePendingStateWrite(storage =>
          storage.applyLocalMutation(
            [node],
            this.metadataPendingMutation(storage, {
              localMutationId: `folder-create:${folder.id}`,
              nodeId: folder.id,
              op: 'create',
              patch: node,
              createdAt: new Date().toISOString(),
            })
          )
        );
      }
    }
  }

  optimisticRenameFolder(folderId: string, name: string): boolean {
    const folder = this.getCachedFolder(folderId);
    if (!folder) {
      return false;
    }
    this.optimisticMoveFolder(folderId, folder.parentId, { name });
    return true;
  }

  /**
   * Apply scalar folder settings returned by the updateFolder mutation.
   *
   * The response is already server-authoritative. Re-fetching the complete
   * folder projection here would turn one settings toggle into a workspace-
   * wide refresh. Permission events still drive scoped authoritative reads.
   */
  applyServerConfirmedFolderSettings(
    folderId: string,
    settings: {
      visibility: string;
      inheritPermissions: boolean;
      updatedAt: string;
    }
  ): void {
    const current = this.getCachedFolder(folderId);
    if (!current) {
      return;
    }
    if (
      current.visibility === settings.visibility &&
      current.inheritPermissions === settings.inheritPermissions
    ) {
      return;
    }

    const revision = settings.updatedAt || current.revision;
    const update = (folder: AccessibleFolderMeta): AccessibleFolderMeta => ({
      ...folder,
      visibility: settings.visibility,
      inheritPermissions: settings.inheritPermissions,
      revision,
    });
    const next = this.cache$.value.map(folder =>
      folder.id === folderId ? update(folder) : folder
    );
    this.updateOptimisticCreatedFolder(folderId, update);
    this.cache$.setValue(next);
    this.saveOrderState(next);

    const workspaceId = this.workspaceId;
    const folder = next.find(candidate => candidate.id === folderId);
    if (!workspaceId || !folder) {
      return;
    }
    const node = this.folderMetaToMetadataNode(workspaceId, folder);
    this.queuePendingStateWrite(storage =>
      storage.applyServerNodes([node], {
        token: storage.getShapeState().token,
        cursor: storage.getShapeState().cursor,
        pageCursors: storage.getShapeState().pageCursors,
      })
    );
  }

  optimisticMoveFolder(
    folderId: string,
    parentId: string | null,
    updates: Partial<
      Pick<
        AccessibleFolderMeta,
        'visibility' | 'inheritPermissions' | 'systemKind' | 'name'
      >
    > = {},
    position?: { at: 'before' | 'after'; folderId: string },
    createIfMissing?: OptimisticFolderCreateIfMissing
  ): void {
    const previousFolder = this.cache$.value.find(
      folder => folder.id === folderId
    );
    let changed = false;
    let next = this.cache$.value.map(folder => {
      if (folder.id !== folderId) {
        return folder;
      }
      changed = true;
      return {
        ...folder,
        ...updates,
        parentId,
      };
    });

    if (!changed) {
      if (!createIfMissing) {
        return;
      }
      changed = true;
      next = [
        {
          id: folderId,
          name: createIfMissing.name,
          parentId,
          ownerUserId: createIfMissing.ownerUserId ?? null,
          systemKind: createIfMissing.systemKind ?? null,
          visibility:
            createIfMissing.visibility ?? updates.visibility ?? 'public',
          inheritPermissions:
            createIfMissing.inheritPermissions ??
            updates.inheritPermissions ??
            true,
          isWorkspaceRoot: false,
          immutable: false,
          storageLimitBytes: null,
          storageUsedBytes: null,
          metadataOnly: false,
          docIds: [],
        },
        ...this.cache$.value,
      ];
    }

    if (position) {
      const moving = next.find(folder => folder.id === folderId);
      const referenceIndex = next.findIndex(
        folder => folder.id === position.folderId
      );
      if (moving && referenceIndex !== -1 && moving.id !== position.folderId) {
        next = next.filter(folder => folder.id !== folderId);
        const nextReferenceIndex = next.findIndex(
          folder => folder.id === position.folderId
        );
        const insertIndex =
          position.at === 'after' ? nextReferenceIndex + 1 : nextReferenceIndex;
        next.splice(insertIndex, 0, moving);
      }
    }

    this.updateOptimisticCreatedFolder(folderId, folder => ({
      ...folder,
      ...updates,
      parentId,
    }));

    this.cache$.setValue(next);
    this.saveOrderState(next);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);

    const wsId = this.workspaceId;
    if (wsId) {
      const folder = next.find(candidate => candidate.id === folderId);
      if (folder) {
        const node = this.folderMetaToMetadataNode(wsId, folder);
        const renamedOnly =
          previousFolder?.parentId === folder.parentId &&
          previousFolder.name !== folder.name &&
          Object.keys(updates).every(key => key === 'name');
        const op = renamedOnly ? 'rename' : 'move';
        const patch: Partial<WorkspaceListMetadata> = renamedOnly
          ? { title: folder.name }
          : { parentId: folder.parentId };
        this.queuePendingStateWrite(storage =>
          storage.applyLocalMutation(
            [node],
            this.metadataPendingMutation(storage, {
              localMutationId: `folder-${op}:${folder.id}`,
              nodeId: folder.id,
              op,
              patch,
              createdAt: new Date().toISOString(),
            })
          )
        );
      }
    }
  }

  /**
   * Optimistically remove a folder subtree from the accessible-folder cache.
   * Normal folder actions keep unrelated doc links untouched.
   */
  optimisticRemoveFolder(folderId: string): void {
    const folders = this.cache$.value;
    const removeIds = new Set<string>([folderId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const folder of folders) {
        if (
          folder.parentId &&
          removeIds.has(folder.parentId) &&
          !removeIds.has(folder.id)
        ) {
          removeIds.add(folder.id);
          changed = true;
        }
      }
    }

    const removedFolders = folders.filter(folder => removeIds.has(folder.id));
    const next = folders.filter(folder => !removeIds.has(folder.id));
    if (next.length === folders.length) {
      return;
    }

    this.cache$.setValue(next);
    this.saveOrderState(next);

    const wsId = this.workspaceId;
    if (wsId) {
      this.persistFolderDeleteMutations(wsId, removedFolders);
    }
  }

  optimisticTrashFolder(
    folderId: string,
    yjsFallbackFolders: AccessibleFolderMeta[] = []
  ): AccessibleFolderOptimisticTrashRecord | null {
    const folders = this.cache$.value;
    const sourceFoldersById = new Map<string, AccessibleFolderMeta>();
    for (const folder of yjsFallbackFolders) {
      sourceFoldersById.set(folder.id, folder);
    }
    for (const folder of folders) {
      sourceFoldersById.set(folder.id, folder);
    }
    const sourceFolders = [...sourceFoldersById.values()];
    const rootFolder = sourceFoldersById.get(folderId);
    const wsId = this.workspaceId;
    if (!rootFolder || !wsId) {
      this.optimisticRemoveFolder(folderId);
      return null;
    }

    const folderIds = new Set<string>([folderId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const folder of sourceFolders) {
        if (
          folder.parentId &&
          folderIds.has(folder.parentId) &&
          !folderIds.has(folder.id)
        ) {
          folderIds.add(folder.id);
          changed = true;
        }
      }
    }

    const subtree = sourceFolders
      .filter(folder => folderIds.has(folder.id))
      .map(folder => this.cloneFolderMeta(folder));
    const docIds = [
      ...new Set(subtree.flatMap(folder => folder.docIds)),
    ].filter(docId => typeof docId === 'string');
    const record: AccessibleFolderOptimisticTrashRecord = {
      id: rootFolder.id,
      workspaceId: wsId,
      parentId: rootFolder.parentId,
      name: rootFolder.name,
      docIds,
      folders: subtree,
      trashedAt: new Date().toISOString(),
    };

    const records = this.loadOptimisticTrashState().filter(
      existing => existing.id !== folderId
    );
    this.setOptimisticTrashRecords([record, ...records]);

    const next = folders.filter(folder => !folderIds.has(folder.id));
    this.cache$.setValue(next);
    this.saveOrderState(next);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);

    const currentRootDocIds = this.rootFolderDocIds$.value;
    const nextRootDocIds = currentRootDocIds.filter(
      docId => !docIds.includes(docId)
    );
    if (nextRootDocIds.length !== currentRootDocIds.length) {
      this.rootFolderDocIds$.setValue(nextRootDocIds);
    }

    if (nextRootDocIds.length !== currentRootDocIds.length) {
      this.saveRootFolderDocIdsToIDB(nextRootDocIds);
    }

    return this.cloneTrashRecord(record);
  }

  restoreOptimisticTrashedFolder(folderId: string): void {
    const records = this.loadOptimisticTrashState();
    const record = records.find(candidate => candidate.id === folderId);
    if (!record) {
      return;
    }

    this.setOptimisticTrashRecords(
      records.filter(candidate => candidate.id !== folderId)
    );

    const existingIds = new Set(this.cache$.value.map(folder => folder.id));
    const restoredFolders = record.folders
      .filter(folder => !existingIds.has(folder.id))
      .map(folder => this.cloneFolderMeta(folder));
    if (restoredFolders.length === 0) {
      return;
    }

    const next = [...restoredFolders, ...this.cache$.value];
    this.cache$.setValue(next);
    this.saveOrderState(next);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);

    const wsId = this.workspaceId;
    if (!wsId) return;
  }

  clearOptimisticTrashedFolder(
    folderId: string,
    outcome: 'confirmed' | 'rejected' = 'confirmed'
  ): void {
    const records = this.loadOptimisticTrashState();
    const record = this.findOptimisticTrashRecordForFolder(folderId);
    if (!record) {
      return;
    }
    this.optimisticTrashedFolders$.setValue(
      records
        .filter(candidate => candidate.id !== record.id)
        .map(candidate => this.cloneTrashRecord(candidate))
    );
    this.queuePendingStateWrite(storage =>
      outcome === 'confirmed'
        ? storage.removeNodes(record.folders.map(folder => folder.id))
        : storage.rejectPending(`folder-trash:${record.id}`)
    );
  }

  rejectPendingFolderMutation(
    operation: string,
    payload: { folderId?: string }
  ): void {
    const folderId = payload.folderId;
    if (!folderId) return;
    const op =
      operation === 'folder.create'
        ? 'create'
        : operation === 'folder.rename'
          ? 'rename'
          : operation === 'folder.move'
            ? 'move'
            : null;
    if (!op) return;

    if (op === 'create') {
      this.setOptimisticCreatedFolders(
        this.optimisticCreatedFolders$.value.filter(
          folder => folder.id !== folderId
        )
      );
      this.cache$.setValue(
        this.cache$.value.filter(folder => folder.id !== folderId)
      );
      void this.authorityStore?.rejectPendingLocalResourceOwner(
        'folder',
        folderId,
        `folder-create:${folderId}`
      );
    }
    this.queuePendingStateWrite(storage =>
      storage.rejectPendingForNode(folderId, [op])
    );
  }

  /**
   * Optimistically hide a server-authoritative folder trash/delete.
   * The cache is the navigation source for members and the private/public
   * filter source for owner/admin. Remove the whole subtree plus contained doc
   * links so sidebar state changes immediately while the backend mutation runs.
   */
  optimisticRemoveFolderSubtree(folderId: string): void {
    this.removeFolderSubtreeFromProjection(folderId, 'pending-delete');
  }

  private removeAuthoritativeFolderSubtree(folderId: string): void {
    this.removeFolderSubtreeFromProjection(folderId, 'authoritative-remove');
  }

  removeRevokedFolderSubtreesLocally(folderIds: readonly string[]): string[] {
    const removedDocIds = new Set<string>();
    for (const folderId of new Set(folderIds)) {
      for (const docId of this.removeFolderSubtreeFromProjection(
        folderId,
        'authoritative-remove'
      )) {
        removedDocIds.add(docId);
      }
    }
    return [...removedDocIds];
  }

  private folderSubtreeIds(folderIds: readonly string[]): Set<string> {
    const folders = this.cache$.value;
    const removedFolderIds = new Set(folderIds);
    let changed = false;

    do {
      changed = false;
      for (const folder of folders) {
        if (
          folder.parentId &&
          removedFolderIds.has(folder.parentId) &&
          !removedFolderIds.has(folder.id)
        ) {
          removedFolderIds.add(folder.id);
          changed = true;
        }
      }
    } while (changed);

    return removedFolderIds;
  }

  private removeFolderSubtreeFromProjection(
    folderId: string,
    persistence: 'pending-delete' | 'authoritative-remove'
  ): string[] {
    const folders = this.cache$.value;
    const removedFolderIds = this.folderSubtreeIds([folderId]);

    const removedDocIds = new Set<string>();
    for (const folder of folders) {
      if (!removedFolderIds.has(folder.id)) {
        continue;
      }
      for (const docId of folder.docIds) {
        removedDocIds.add(docId);
      }
    }

    let cacheChanged = false;
    const updatedFolders = folders
      .filter(folder => {
        const keep = !removedFolderIds.has(folder.id);
        if (!keep) cacheChanged = true;
        return keep;
      })
      .map(folder => {
        if (
          removedDocIds.size === 0 ||
          !folder.docIds.some(docId => removedDocIds.has(docId))
        ) {
          return folder;
        }
        cacheChanged = true;
        return {
          ...folder,
          docIds: folder.docIds.filter(docId => !removedDocIds.has(docId)),
        };
      });

    const currentRootDocIds = this.rootFolderDocIds$.value;
    const updatedRootDocIds =
      removedDocIds.size === 0
        ? currentRootDocIds
        : currentRootDocIds.filter(docId => !removedDocIds.has(docId));
    const rootChanged = updatedRootDocIds.length !== currentRootDocIds.length;

    if (!cacheChanged && !rootChanged) {
      return [];
    }

    if (cacheChanged) {
      this.cache$.setValue(updatedFolders);
      this.saveOrderState(updatedFolders);
    }
    if (rootChanged) {
      this.rootFolderDocIds$.setValue(updatedRootDocIds);
    }

    const wsId = this.workspaceId;
    if (!wsId) {
      return [...removedDocIds];
    }
    if (cacheChanged) {
      if (persistence === 'pending-delete') {
        this.persistFolderDeleteMutations(
          wsId,
          folders.filter(folder => removedFolderIds.has(folder.id))
        );
      } else {
        this.queuePendingStateWrite(storage =>
          storage.removeNodes([...removedFolderIds])
        );
      }
    }
    if (rootChanged) {
      this.saveRootFolderDocIdsToIDB(updatedRootDocIds);
    }
    return [...removedDocIds];
  }

  optimisticAddDocToFolder(
    folderId: string,
    docId: string,
    position?: { at: 'before' | 'after'; docId: string }
  ): void {
    this.recordPendingDocFolderAssignment(folderId, docId);

    const rootDocIds = this.rootFolderDocIds$.value.filter(id => id !== docId);
    let changed = rootDocIds.length !== this.rootFolderDocIds$.value.length;

    const folders = this.cache$.value.map(folder => {
      const withoutDoc = folder.docIds.filter(id => id !== docId);
      let nextDocIds = withoutDoc;
      if (folder.id === folderId && !withoutDoc.includes(docId)) {
        nextDocIds = insertDocAtPosition(withoutDoc, docId, position);
      }

      if (
        nextDocIds.length !== folder.docIds.length ||
        nextDocIds.some((id, index) => id !== folder.docIds[index])
      ) {
        changed = true;
        return { ...folder, docIds: nextDocIds };
      }

      return folder;
    });

    if (!changed) {
      return;
    }

    this.cache$.setValue(folders);
    this.saveOrderState(folders);
    this.rootFolderDocIds$.setValue(rootDocIds);

    const wsId = this.workspaceId;
    if (wsId) {
      this.saveLocalFolderStateToIDB(folders);
      this.saveRootFolderDocIdsToIDB(rootDocIds);
    }
  }

  recordPendingDocFolderAssignment(folderId: string, docId: string): void {
    const assignments = this.loadPendingDocAssignments();
    this.setPendingDocAssignments([
      ...assignments.filter(assignment => assignment.docId !== docId),
      { docId, folderId, createdAt: Date.now() },
    ]);
  }

  clearPendingDocFolderAssignment(docId: string): void {
    const assignments = this.loadPendingDocAssignments();
    if (!assignments.some(assignment => assignment.docId === docId)) {
      return;
    }
    this.setPendingDocAssignments(
      assignments.filter(assignment => assignment.docId !== docId)
    );
  }

  flushPendingStateWrites(): Promise<void> {
    return this.pendingStateWrite;
  }

  captureOptimisticSnapshot(): AccessibleFolderOptimisticSnapshot {
    return {
      folders: this.cache$.value.map(folder => ({
        ...folder,
        docIds: [...folder.docIds],
      })),
      rootFolderDocIds: [...this.rootFolderDocIds$.value],
      optimisticCreatedFolders: this.optimisticCreatedFolders$.value.map(
        folder => this.cloneFolderMeta(folder)
      ),
      pendingDocFolderAssignments: this.loadPendingDocAssignments().map(
        assignment => this.clonePendingDocAssignment(assignment)
      ),
    };
  }

  restoreOptimisticSnapshot(
    snapshot: AccessibleFolderOptimisticSnapshot
  ): void {
    const currentFolders = this.cache$.value;
    const folders = snapshot.folders.map(folder => ({
      ...folder,
      docIds: [...folder.docIds],
    }));
    const rootFolderDocIds = [...snapshot.rootFolderDocIds];

    this.cache$.setValue(folders);
    this.saveOrderState(folders);
    this.rootFolderDocIds$.setValue(rootFolderDocIds);
    this.setOptimisticCreatedFolders(snapshot.optimisticCreatedFolders ?? []);
    this.setPendingDocAssignments(snapshot.pendingDocFolderAssignments ?? []);

    const wsId = this.workspaceId;
    if (!wsId) {
      return;
    }
    const snapshotById = new Map(folders.map(folder => [folder.id, folder]));
    const currentById = new Map(
      currentFolders.map(folder => [folder.id, folder])
    );
    const changedFolderIds = new Set<string>();
    for (const folderId of new Set([
      ...snapshotById.keys(),
      ...currentById.keys(),
    ])) {
      const before = snapshotById.get(folderId);
      const after = currentById.get(folderId);
      if (
        !before ||
        !after ||
        before.name !== after.name ||
        before.parentId !== after.parentId ||
        before.visibility !== after.visibility ||
        before.inheritPermissions !== after.inheritPermissions
      ) {
        changedFolderIds.add(folderId);
      }
    }
    if (changedFolderIds.size > 0) {
      this.queuePendingStateWrite(storage =>
        storage.rejectPendingForNodes(
          [...changedFolderIds],
          ['create', 'move', 'rename', 'trash', 'restore', 'delete']
        )
      );
    }
    this.saveRootFolderDocIdsToIDB(rootFolderDocIds);
  }

  /**
   * Optimistically expose a locally-created root doc before the async
   * root-folder mutation has created the server-side folder link. Also used by
   * root-level doc moves, where the visible root list is owned by this cache
   * rather than by the Y.js folder tree.
   */
  optimisticAddToRoot(docId: string): void {
    const rootFolderId = this.cache$.value.find(
      folder => folder.isWorkspaceRoot
    )?.id;
    if (rootFolderId) {
      this.recordPendingDocFolderAssignment(rootFolderId, docId);
    } else {
      this.clearPendingDocFolderAssignment(docId);
    }

    const current = this.rootFolderDocIds$.value;
    const updatedRootDocIds = current.includes(docId)
      ? current
      : [docId, ...current];
    let foldersChanged = false;
    const updatedFolders = this.cache$.value.map(folder => {
      if (!folder.docIds.includes(docId)) {
        return folder;
      }
      foldersChanged = true;
      return {
        ...folder,
        docIds: folder.docIds.filter(id => id !== docId),
      };
    });
    const rootChanged = updatedRootDocIds.length !== current.length;

    if (!foldersChanged && !rootChanged) {
      return;
    }

    if (foldersChanged) {
      this.cache$.setValue(updatedFolders);
      this.saveOrderState(updatedFolders);
    }
    if (rootChanged) {
      this.rootFolderDocIds$.setValue(updatedRootDocIds);
    }

    const wsId = this.workspaceId;
    if (wsId) {
      if (foldersChanged) {
        this.saveLocalFolderStateToIDB(updatedFolders);
      }
      if (rootChanged) {
        this.saveRootFolderDocIdsToIDB(updatedRootDocIds);
      }
    }
  }
}
